import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import {
  X, Wind, CircleDashed, Waves, Swords, Lightbulb, Move, Heart, Sparkles, Share2,
  Aperture, RotateCw, Play, Square, RotateCcw, Droplets, Hammer, Layers, 
  LayoutGrid, StretchHorizontal, FileText, ShieldAlert, Smile, Clock, Gavel, Moon, Sun,
  ArrowUpCircle, Coffee, CircleHelp, ShoppingBag, Copy, Download, ShieldCheck, Users,
  Ban, VolumeX, Smartphone, Menu, List, Home, BookOpen, Info, Lock, 
  WifiOff
} from "lucide-react";

// --- Types ---
type GameType =
  | "pebble" 
  | "smiley" | "bubble-pop" | "fidget-spinner" | "stone-skip" | "punching-bag"
  | "bulb" | "newton" | "breath" | "fireworks" | "gravity" | "kaleidoscope"
  | "ripple" | "vase" | "peel" | "duck-pond" | "trampoline" | "paper-boat" | null;

type ViewMode = "carousel" | "grid";

interface GameCardProps {
  title: string; description: string; color: string; bgClass: string;
  icon: React.ReactNode; thumbnail?: React.ReactNode; onClick: () => void;
  viewMode: ViewMode; isDarkMode: boolean; isNative: boolean;
}

// --- Custom Icons ---
const DuckIcon = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="10" cy="7" r="1.5" fill="currentColor" stroke="none" />
    <path d="M13 13c1.5 0 3.5-.8 3.5-3" /><path d="M5.5 8.5c-.8 0-2 .5-2.5 1.5s.5 2.5 2 2.5c.5 3 3 6.5 8 6.5 4 0 7-3 7-6.5 0-2.5-2-4-4-4-.5-3-3-5.5-6-5.5-2.5 0-5 2-5 5h.5z" />
  </svg>
);

const CustomLogo = ({ className }: { className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 505.69 505.69" className={className}>
    <defs><linearGradient id="logo-gradient" x1="51.8" y1="-1.81" x2="495.82" y2="560.61" gradientUnits="userSpaceOnUse"><stop offset="0" stopColor="#e5e51c" /><stop offset=".2" stopColor="#e1e41c" /><stop offset=".35" stopColor="#d8e11c" /><stop offset=".48" stopColor="#c8dc1c" /><stop offset=".61" stopColor="#b1d51d" /><stop offset=".72" stopColor="#93cd1e" /><stop offset=".84" stopColor="#6ec21f" /><stop offset=".95" stopColor="#44b621" /><stop offset="1" stopColor="#2caf22" /></linearGradient></defs>
    <g id="bg"><rect fill="url(#logo-gradient)" width="505.69" height="505.69" rx="130.91" ry="130.91"/></g>
    <g id="Layer_7"><path fill="#15575f" d="M199.34,429.41s-32.81-8.9-42.82-21.69-25.58-38.92-11.4-59.5,25.3-28.36,40.59-28.36,84.24,13.07,122.89,31.7,60.05,26.97,65.06,47.82-6.12,33.36-40.59,36.14-99.53,1.39-133.73-6.12Z"/><path fill="#2a8689" d="M160.6,410.28s47.29,6.31,84.86,6.31,72.35-4.87,72.35-18.78-11.13-29.92-66.09-49.4c-54.96-19.48-91.83-18.09-91.83-18.09,0,0-26.14,20.81-22.95,52.72,1.38,13.76,23.66,27.24,23.66,27.24Z"/><path fill="#ea8325" d="M157.41,312.09s-55.72-18.07-61.74-33.88-14.68-42.92,12.8-51.58,64.38-16.94,128.38,1.51,90.35,24.85,123.11,55.34,16.94,64-18.82,62.49-40.66,0-93.74-13.55-89.98-20.33-89.98-20.33Z"/><path fill="#f69d2a" d="M99.42,283.94s15.95,14.38,33.34,15.08,36.18,12.52,64.01,12.52,89.05,6.96,98.1-3.48,29.22-15.31,6.96-35.48c-22.26-20.18-52.18-25.74-68.88-32s-13.22-22.26-39.66-22.96-83.57-2.75-96.79,15.34-15.17,30.11,2.92,50.98Z"/><path fill="#393546" d="M174.92,182.75s33.32,37.27,67.76,46.87,66.64,25.98,105.6,14.68c38.96-11.29,54.21-28.24,26.54-60.42s-60.99-36.14-86.4-41.79-85.84-10.73-99.39-.56-21.46,27.67-14.12,41.22Z"/><path fill="#594b63" d="M221.36,137.94s73.48,14.17,85.55,20.99,44.09,27.03,34.9,40.68-14.7,24.41-55.63,21.26-79.78-19.16-89.49-22.31-21.79-12.94-23.89-19.5-1.56-21.44,5.79-28.26,27.82-16.01,42.78-12.86Z"/><path fill="#ea8325" d="M313.85,141.12s37.91.97,50.07-22.85,17.5-43.75-.97-55.41-46.18-9.72-74.37,2.92-54.44,23.33-51.52,51.52,64.16,25.28,76.8,23.82Z"/><path fill="#f69d2a" d="M237.09,106.59s-1.41,16.3,10.12,17.99,47.79,5.06,63.53-1.41,28.96-13.49,30.08-23.05,9.84-21.65,9.84-29.52-13.49-17.99-32.61-13.49-51.73,19.96-59.6,27.27-21.37,22.21-21.37,22.21Z"/></g>
  </svg>
);

// New unified SVG Badge component
// --- NEW Google Play Badge (Full Button SVG) ---
const GooglePlayBadge = ({ className }: { className?: string }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 238.96 70.87"
    role="img"
    aria-label="Get it on Google Play"
  >
    <rect x="-.11" width="239.17" height="70.87" rx="8.86" ry="8.86" fill="#000" />
    <path fill="#a6a6a6" d="M230.21,1.42c4.1,0,7.44,3.34,7.44,7.44v53.15c0,4.1-3.34,7.44-7.44,7.44H8.75c-4.1,0-7.44-3.34-7.44-7.44V8.86C1.31,4.76,4.65,1.42,8.75,1.42h221.46M230.21,0H8.75C3.88,0-.11,3.99-.11,8.86v53.15c0,4.87,3.99,8.86,8.86,8.86h221.46c4.87,0,8.86-3.99,8.86-8.86V8.86c0-4.87-3.99-8.86-8.86-8.86h0Z"/>
    <g stroke="#fff" strokeMiterlimit="10" strokeWidth="0.2px" fill="#fff">
      <path d="M83.9,18.15c0,1.48-.44,2.67-1.32,3.55-1,1.05-2.3,1.57-3.9,1.57s-2.84-.53-3.91-1.59c-1.07-1.06-1.61-2.38-1.61-3.96s.54-2.89,1.61-3.96c1.07-1.06,2.38-1.6,3.91-1.6.76,0,1.49.15,2.18.44.69.3,1.25.69,1.66,1.19l-.93.94c-.7-.84-1.67-1.26-2.91-1.26-1.12,0-2.09.39-2.9,1.18s-1.22,1.81-1.22,3.06.41,2.28,1.22,3.06c.82.79,1.78,1.18,2.9,1.18,1.19,0,2.18-.4,2.97-1.19.51-.52.81-1.23.89-2.15h-3.86v-1.28h5.15c.05.28.07.54.07.8Z"/>
      <path d="M92.07,13.71h-4.84v3.37h4.37v1.28h-4.37v3.37h4.84v1.31h-6.21v-10.63h6.21v1.31h0Z"/>
      <path d="M97.83,23.03h-1.37v-9.32h-2.97v-1.31h7.3v1.31h-2.97s0,9.32,0,9.32Z"/>
      <path d="M106.08,23.03v-10.63h1.36v10.63h-1.36Z"/>
      <path d="M113.51,23.03h-1.37v-9.32h-2.97v-1.31h7.3v1.31h-2.97s0,9.32,0,9.32Z"/>
      <path d="M130.3,21.66c-1.05,1.08-2.34,1.61-3.9,1.61s-2.85-.54-3.9-1.61c-1.05-1.07-1.57-2.39-1.57-3.94s.52-2.87,1.57-3.94c1.04-1.08,2.34-1.61,3.9-1.61s2.84.54,3.89,1.62c1.05,1.08,1.57,2.39,1.57,3.93s-.52,2.87-1.57,3.94ZM123.52,20.77c.79.8,1.75,1.19,2.89,1.19s2.1-.4,2.89-1.19c.79-.8,1.18-1.81,1.18-3.05s-.4-2.26-1.18-3.05c-.78-.8-1.75-1.19-2.89-1.19s-2.1.4-2.89,1.19c-.78.8-1.18,1.81-1.18,3.05s.4,2.26,1.18,3.05Z"/>
      <path d="M133.79,23.03v-10.63h1.66l5.17,8.27h.06l-.06-2.05v-6.22h1.37v10.63h-1.43l-5.41-8.67h-.06l.06,2.05v6.62h-1.37,0Z"/>
    </g>
    <path fill="#fff" d="M120.61,38.54c-4.17,0-7.56,3.17-7.56,7.53s3.4,7.53,7.56,7.53,7.56-3.2,7.56-7.53-3.4-7.53-7.56-7.53ZM120.61,50.64c-2.28,0-4.25-1.88-4.25-4.57s1.97-4.57,4.25-4.57,4.25,1.85,4.25,4.57-1.97,4.57-4.25,4.57ZM104.11,38.54c-4.17,0-7.56,3.17-7.56,7.53s3.4,7.53,7.56,7.53,7.56-3.2,7.56-7.53-3.4-7.53-7.56-7.53ZM104.11,50.64c-2.28,0-4.25-1.88-4.25-4.57s1.97-4.57,4.25-4.57,4.25,1.85,4.25,4.57-1.97,4.57-4.25,4.57ZM84.48,40.85v3.2h7.65c-.23,1.8-.83,3.11-1.74,4.03-1.11,1.11-2.85,2.34-5.91,2.34-4.71,0-8.39-3.8-8.39-8.51s3.68-8.51,8.39-8.51c2.54,0,4.4,1,5.76,2.28l2.26-2.26c-1.91-1.83-4.45-3.22-8.02-3.22-6.45,0-11.87,5.25-11.87,11.7s5.42,11.7,11.87,11.7c3.48,0,6.11-1.14,8.16-3.28,2.11-2.11,2.77-5.08,2.77-7.48,0-.74-.06-1.43-.17-2h-10.76,0ZM164.75,43.33c-.63-1.68-2.54-4.8-6.45-4.8s-7.11,3.05-7.11,7.53c0,4.22,3.2,7.53,7.48,7.53,3.45,0,5.45-2.11,6.28-3.34l-2.57-1.71c-.86,1.26-2.03,2.08-3.71,2.08s-2.88-.77-3.65-2.28l10.08-4.17-.34-.86h0ZM154.47,45.84c-.09-2.91,2.26-4.4,3.94-4.4,1.31,0,2.43.66,2.8,1.6l-6.74,2.8ZM146.28,53.15h3.31v-22.15h-3.31v22.15ZM140.86,40.22h-.11c-.74-.88-2.17-1.68-3.97-1.68-3.77,0-7.22,3.31-7.22,7.56s3.45,7.51,7.22,7.51c1.8,0,3.22-.8,3.97-1.71h.11v1.08c0,2.88-1.54,4.42-4.03,4.42-2.03,0-3.28-1.46-3.8-2.68l-2.88,1.2c.83,2,3.03,4.45,6.68,4.45,3.88,0,7.16-2.28,7.16-7.85v-13.53h-3.14v1.23ZM137.06,50.64c-2.28,0-4.2-1.91-4.2-4.54s1.91-4.6,4.2-4.6,4.03,1.94,4.03,4.6-1.77,4.54-4.03,4.54ZM180.26,31h-7.92v22.15h3.31v-8.39h4.62c3.66,0,7.27-2.65,7.27-6.88s-3.6-6.88-7.27-6.88ZM180.34,41.68h-4.7v-7.59h4.7c2.47,0,3.87,2.05,3.87,3.8s-1.4,3.8-3.87,3.8h0ZM200.77,38.5c-2.39,0-4.87,1.05-5.9,3.39l2.94,1.23c.63-1.23,1.79-1.62,3.02-1.62,1.71,0,3.45,1.03,3.48,2.85v.23c-.6-.34-1.88-.86-3.45-.86-3.16,0-6.38,1.74-6.38,4.99,0,2.96,2.59,4.87,5.5,4.87,2.22,0,3.45-1,4.22-2.16h.11v1.71h3.19v-8.49c0-3.93-2.94-6.13-6.73-6.13h0ZM200.38,50.63c-1.08,0-2.59-.54-2.59-1.88,0-1.71,1.88-2.37,3.5-2.37,1.45,0,2.14.31,3.02.74-.26,2.05-2.02,3.5-3.93,3.5ZM219.12,38.98l-3.79,9.6h-.11l-3.93-9.6h-3.56l5.9,13.42-3.36,7.47h3.45l9.09-20.89h-3.68ZM189.35,53.15h3.31v-22.15h-3.31v22.15Z"/>
    <g>
      <path fill="#ea4335" d="M36.6,34.41l-18.86,20.02s0,0,0,.01c.58,2.17,2.56,3.77,4.92,3.77.94,0,1.83-.26,2.58-.7l.06-.04,21.23-12.25-9.94-10.82Z"/>
      <path fill="#fbbc04" d="M55.68,31h-.02s-9.17-5.33-9.17-5.33l-10.33,9.19,10.36,10.36,9.12-5.26c1.6-.86,2.68-2.55,2.68-4.49s-1.07-3.61-2.65-4.47h0Z"/>
      <path fill="#4285f4" d="M17.73,16.44c-.11.42-.17.86-.17,1.31v35.38c0,.45.06.89.17,1.31l19.51-19.51-19.51-18.49Z"/>
      <path fill="#34a853" d="M36.74,35.43l9.76-9.76-21.21-12.3c-.77-.46-1.67-.73-2.63-.73-2.36,0-4.34,1.6-4.92,3.78h0s19,19,19,19h0Z"/>
    </g>
  </svg>
);

const AppleIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.564 13.862c-.413.916-.612 1.325-1.144 2.135-.742 1.13-1.79 2.538-3.087 2.55-1.152.01-1.448-.75-3.013-.741-1.564.008-1.89.755-3.043.744-1.297-.012-2.29-1.283-3.033-2.414-2.077-3.16-2.294-6.87-1.013-8.843.91-1.401 2.347-2.221 3.697-2.221 1.375 0 2.24.754 3.376.754 1.103 0 1.775-.756 3.365-.756 1.2 0 2.474.655 3.381 1.785-2.972 1.629-2.49 5.873.514 7.007zM12.463 3.808c.577-.742 1.016-1.788.857-2.858-.944.065-2.047.665-2.692 1.448-.584.71-1.067 1.763-.88 2.787 1.03.031 2.096-.584 2.715-1.377z"/>
  </svg>
);

// --- STATIC THUMBNAILS (No Animation) ---

const PebbleMindThumbnail = ({ isDarkMode }: { isDarkMode: boolean }) => (
  <svg viewBox="0 0 300 400" className="w-full h-full" preserveAspectRatio="xMidYMid slice">
    <g transform="translate(110, 200)">
       {/* Shadow */}
       <ellipse cx="0" cy="50" rx="70" ry="12" fill="#000" opacity="0.1" />
       
       {/* Stone */}
       <path d="M-50 10 Q-60 -20 0 -30 Q60 -20 50 10 Q0 40 -50 10" fill={isDarkMode ? "#57534e" : "#a8a29e"} stroke={isDarkMode ? "#292524" : "#78716c"} strokeWidth="3" />
       
       {/* Butterfly - Using GAME paths (centered at 50,50) */}
       {/* Adjusted translate to align 50,50 center to the stone top */}
       <g transform="translate(-50, -100) rotate(-10 50 50) scale(1.8)">
          {/* Wings */}
          <path d="M50 50 C 20 20, 0 10, 10 50 C 0 80, 20 80, 50 50" fill="#f59e0b" stroke="#b45309" strokeWidth="1.5" />
          <path d="M50 50 C 80 20, 100 10, 90 50 C 100 80, 80 80, 50 50" fill="#f59e0b" stroke="#b45309" strokeWidth="1.5" />
          
          {/* Body */}
          <ellipse cx="50" cy="50" rx="3" ry="15" fill="#451a03" />
          
          {/* Antennae */}
          <path d="M50 38 L 42 25" stroke="#451a03" strokeWidth="1" opacity="0.6" />
          <path d="M50 38 L 58 25" stroke="#451a03" strokeWidth="1" opacity="0.6" />
       </g>
    </g>
  </svg>
);

const TrampolineThumbnail = ({ isDarkMode }: { isDarkMode: boolean }) => (
  <svg viewBox="0 0 300 400" className="w-full h-full" preserveAspectRatio="xMidYMid slice">
    {/* Floor Line */}
    <path d="M20 340 Q150 340 280 340" stroke={isDarkMode ? "#475569" : "#94a3b8"} strokeWidth="4" strokeLinecap="round" />
    
    {/* Trampoline - Centered at bottom */}
    <g transform="translate(150, 275)">
       <path d="M-80 0 L-80 30" stroke={isDarkMode ? "#64748b" : "#60a5fa"} strokeWidth="6" strokeLinecap="round" />
       <path d="M80 0 L80 30" stroke={isDarkMode ? "#64748b" : "#60a5fa"} strokeWidth="6" strokeLinecap="round" />
       <path d="M-90 0 Q0 25 90 0" fill="none" stroke={isDarkMode ? "#94a3b8" : "#3b82f6"} strokeWidth="6" strokeLinecap="round" />
    </g>

    {/* Character - Jumping High (Centered Horizontally) */}
    <g transform="translate(150, 180)">
       <circle cx="0" cy="0" r="50" fill="#fde047" stroke={isDarkMode ? "none" : "#ca8a04"} strokeWidth="2" />
       {/* Eyes */}
       <circle cx="-15" cy="-5" r="6" fill="#1e293b" />
       <circle cx="15" cy="-5" r="6" fill="#1e293b" />
       {/* Open Mouth */}
       <path d="M-10 15 A 10 10 0 0 0 10 15 Z" fill="#ef4444" />
       {/* Limbs */}
       <path d="M-35 0 L-55 -20" stroke="#fde047" strokeWidth="8" strokeLinecap="round" />
       <path d="M35 0 L55 -20" stroke="#fde047" strokeWidth="8" strokeLinecap="round" />
       <path d="M-15 35 L-20 60" stroke="#fde047" strokeWidth="8" strokeLinecap="round" />
       <path d="M15 35 L20 60" stroke="#fde047" strokeWidth="8" strokeLinecap="round" />
    </g>
  </svg>
);

const DuckPondThumbnail = ({ isDarkMode }: { isDarkMode: boolean }) => (
  <svg viewBox="0 0 300 400" className="w-full h-full" preserveAspectRatio="xMidYMid slice">
    {/* Duck Centered */}
    <g transform="translate(-20, -30)">
       <path d="M120 250 Q120 290 160 290 L200 290 Q240 290 240 250 Q240 220 200 220 L180 220 L180 200 Q180 170 150 170 Q120 170 120 200 Z" fill="#facc15" stroke={isDarkMode ? "none" : "#ca8a04"} strokeWidth="2" />
       <path d="M160 240 Q190 240 200 260 Q170 270 150 250" fill="#eab308" />
       <path d="M120 190 L100 195 L120 200" fill="#f97316" />
       <circle cx="145" cy="190" r="4" fill="#000" />
    </g>
    {/* Simple Water Lines */}
    <path d="M100 280 Q150 295 200 280" stroke={isDarkMode ? "#60a5fa" : "#2563eb"} strokeWidth="3" fill="none" strokeLinecap="round" opacity="0.6" />
    <path d="M120 300 Q150 310 180 300" stroke={isDarkMode ? "#60a5fa" : "#2563eb"} strokeWidth="3" fill="none" strokeLinecap="round" opacity="0.6" />
  </svg>
);

const BubblePopThumbnail = ({ isDarkMode }: { isDarkMode: boolean }) => (
  <svg viewBox="0 0 300 400" className="w-full h-full" preserveAspectRatio="xMidYMid slice">
    {/* Bubbles Floating - No Box */}
    <g transform="translate(0, 20)">
      <circle cx="150" cy="150" r="50" fill={isDarkMode ? "#0ea5e9" : "#7dd3fc"} opacity="0.8" />
      <circle cx="170" cy="130" r="12" fill="white" opacity="0.4" />
      <circle cx="230" cy="220" r="35" fill={isDarkMode ? "#8b5cf6" : "#c4b5fd"} opacity="0.7" />
      <circle cx="245" cy="210" r="8" fill="white" opacity="0.4" />
      <circle cx="70" cy="240" r="30" fill={isDarkMode ? "#f43f5e" : "#fda4af"} opacity="0.8" />
      <circle cx="250" cy="100" r="20" fill={isDarkMode ? "#10b981" : "#6ee7b7"} opacity="0.6" />
      <circle cx="100" cy="80" r="15" fill={isDarkMode ? "#f59e0b" : "#fcd34d"} opacity="0.6" />
    </g>
  </svg>
);

const FidgetSpinnerThumbnail = ({ isDarkMode }: { isDarkMode: boolean }) => (
  <svg viewBox="0 0 300 400" className="w-full h-full" preserveAspectRatio="xMidYMid slice">
    {/* Scaled 1.5x and moved down to Y=210 to fit red lines */}
    <g transform="translate(150, 230) scale(2.5)">
      <g transform="translate(-50, -50)">
         <g fill="#f43f5e"><circle cx="50" cy="15" r="12" /><circle cx="50" cy="15" r="7" fill="#333" /></g>
         <g fill="#22c55e"><circle cx="19.7" cy="67.5" r="12" /><circle cx="19.7" cy="67.5" r="7" fill="#333" /></g>
         <g fill="#3b82f6"><circle cx="80.3" cy="67.5" r="12" /><circle cx="80.3" cy="67.5" r="7" fill="#333" /></g>
         <path d="M50 50 L50 27 Q50 50 32 58 L50 50 L68 58 Q50 50 50 27 Z" fill="#e2e8f0" stroke={isDarkMode ? "#64748b" : "#94a3b8"} strokeWidth="10" strokeLinecap="round" strokeLinejoin="round" />
         <circle cx="50" cy="50" r="14" fill="#fff" stroke={isDarkMode ? "#475569" : "#64748b"} strokeWidth="2" />
      </g>
    </g>
  </svg>
);

const StoneSkipThumbnail = ({ isDarkMode }: { isDarkMode: boolean }) => {
  const rippleColor = isDarkMode ? "#38bdf8" : "#0ea5e9"; // Cyan/Sky blue
  const stoneColor = isDarkMode ? "#94a3b8" : "#64748b"; // Slate

  return (
    <svg viewBox="0 0 300 400" className="w-full h-full" preserveAspectRatio="xMidYMid slice">
      <g transform="translate(150, 200)">
        
        {/* --- Background Ripples (Far away) --- */}
        {/* Small, faint ripple in the distance */}
        <g transform="translate(-60, -40) scale(0.6)">
           <ellipse rx="50" ry="10" fill="none" stroke={rippleColor} strokeWidth="3" opacity="0.3" />
        </g>

        {/* --- Mid-Ground Ripples --- */}
        {/* Medium ripple, slightly closer */}
        <g transform="translate(40, 20) scale(0.8)">
           <ellipse rx="50" ry="12" fill="none" stroke={rippleColor} strokeWidth="3" opacity="0.5" />
           <ellipse rx="30" ry="6" fill="none" stroke={rippleColor} strokeWidth="2" opacity="0.3" />
        </g>

        {/* --- Foreground Ripple (The Active Splash) --- */}
        {/* Large, distinct ripple below the stone */}
        <g transform="translate(-20, 100)">
           <ellipse rx="70" ry="15" fill="none" stroke={rippleColor} strokeWidth="3" opacity="0.8" />
           <ellipse rx="45" ry="10" fill="none" stroke={rippleColor} strokeWidth="2" opacity="0.6" />
           {/* Splash droplets */}
           <circle cx="-50" cy="0" r="3" fill={rippleColor} opacity="0.6" />
           <circle cx="50" cy="-2" r="4" fill={rippleColor} opacity="0.6" />
        </g>

        {/* --- The Stone (In Flight) --- */}
        {/* Floating above the foreground ripple, tilted for dynamics */}
        <g transform="translate(-20, 70) rotate(-10)">
           {/* Shadow on "water" (simulated by transparency offset) */}
           <ellipse cx="0" cy="40" rx="30" ry="8" fill="#000" opacity="0.1" />
           
           {/* Stone Body */}
           <ellipse cx="0" cy="0" rx="40" ry="15" fill={stoneColor} stroke={isDarkMode ? "none" : "#475569"} strokeWidth="2" />
           {/* Stone Highlight */}
           <path d="M-20 -5 Q0 -8 20 -5" fill="none" stroke="white" strokeWidth="2" opacity="0.3" strokeLinecap="round" />
        </g>

      </g>
    </svg>
  );
};

// 1. FIXED PUNCHING BAG (Scaled down 5% to ensure full visibility)
const PunchingBagThumbnail = ({ isDarkMode }: { isDarkMode: boolean }) => {
  const metalColor = isDarkMode ? "#94a3b8" : "#475569";
  return (
    <svg viewBox="0 0 300 400" className="w-full h-full" preserveAspectRatio="xMidYMid slice">
      {/* Global Scale Down by 7% (0.80) around center (150, 200) to fit perfectly */}
      <g transform="translate(150, 200) scale(0.80) translate(-150, -200)">
          
          {/* Ceiling Mount - Fixed at y=80 */}
          <line x1="100" y1="80" x2="200" y2="80" stroke={metalColor} strokeWidth="6" strokeLinecap="round" />

          {/* Rope - Connected from y=80 to y=170 */}
          <line x1="150" y1="80" x2="150" y2="170" stroke={metalColor} strokeWidth="4" strokeLinecap="round" />
          
          {/* Bag Group - Connected at y=170 */}
          <g transform="translate(150, 170)">
             {/* Connection Ring */}
             <circle cx="0" cy="0" r="10" fill="none" stroke={metalColor} strokeWidth="3" />
             
             {/* Bag Body */}
             <rect x="-35" y="0" width="70" height="160" rx="25" fill="#be123c" stroke={isDarkMode ? "none" : "#881337"} strokeWidth="2" />
             
             {/* Shine */}
             <path d="M-20 30 Q-30 80 -20 120" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="3" />
             
             {/* Impact Star */}
             <g transform="translate(0, 90)">
                <path d="M0 0 L15 -15 L25 5 L40 -5 L30 15 L45 30 L20 25 L0 40 L5 20 L-15 10 Z" fill="#fef08a" stroke="#facc15" strokeWidth="2" transform="translate(-20, -20) scale(0.8)"/>
             </g>
          </g>
          
      </g>
    </svg>
  );
};

const BulbThumbnail = ({ isDarkMode }: { isDarkMode: boolean }) => {
  // Define standardized colors for metal parts
  const metalColor = isDarkMode ? "#4b5563" : "#9ca3af";

  return (
  <svg viewBox="0 0 300 400" className="w-full h-full" preserveAspectRatio="xMidYMid slice">
    {/* Ceiling Mount Bar - Small bar at the top edge (global y=15) */}
    <line x1="140" y1="90" x2="160" y2="90" stroke={metalColor} strokeWidth="6" strokeLinecap="round" />
    
    {/* Floating Bulb Group centered at (150, 210) */}
    <g transform="translate(150, 210)">
       {/* Wire - Extended up to y=-195 to meet the global y=15 mount point (210 - 195 = 15) */}
       <line x1="0" y1="-195" x2="0" y2="-60" stroke={metalColor} strokeWidth="4" />
       
       {/* Minimal Socket */}
       <rect x="-15" y="-60" width="30" height="30" rx="4" fill={metalColor} />
       
       {/* Bulb Body */}
       <circle cx="0" cy="0" r="60" fill="#fde047" stroke={isDarkMode ? "none" : "#eab308"} strokeWidth="2" />
       
       {/* Glow (Blur filter) */}
       <circle cx="0" cy="0" r="85" fill="#facc15" opacity="0.3" style={{ filter: "blur(20px)" }} />
       
       {/* Filament Details */}
       <path d="M-20 -30 Q-30 -10 -20 10" fill="none" stroke="white" strokeWidth="4" opacity="0.6" strokeLinecap="round" />
       <path d="M-10 -50 L-10 -20 L0 -40 L10 -20 L10 -50" fill="none" stroke="#d97706" strokeWidth="2" />
    </g>
  </svg>
  );
};

const NewtonThumbnail = ({ isDarkMode }: { isDarkMode: boolean }) => (
  <svg viewBox="0 0 300 400" className="w-full h-full" preserveAspectRatio="xMidYMid slice">
    {/* Frame Bar - Moved down to Y=130 to connect perfectly with the strings */}
    <line 
      x1="70" y1="130" x2="230" y2="130" 
      stroke={isDarkMode ? "#94a3b8" : "#475569"} 
      strokeWidth="6" 
      strokeLinecap="round" 
    />

    <g transform="translate(150, 280)">
       {/* 1. Left Ball */}
       {/* String starts at y=-150 relative to 280 = Absolute Y 130 (Matches Frame Bar) */}
       <line x1="-60" y1="-150" x2="-60" y2="0" stroke={isDarkMode ? "#cbd5e1" : "#94a3b8"} strokeWidth="2" />
       <circle cx="-60" cy="0" r="18" fill={isDarkMode ? "#64748b" : "#334155"} />
       
       {/* 2. Middle-Left Ball */}
       <line x1="-20" y1="-150" x2="-20" y2="0" stroke={isDarkMode ? "#cbd5e1" : "#94a3b8"} strokeWidth="2" />
       <circle cx="-20" cy="0" r="18" fill={isDarkMode ? "#64748b" : "#334155"} />
       
       {/* 3. Middle-Right Ball */}
       <line x1="20" y1="-150" x2="20" y2="0" stroke={isDarkMode ? "#cbd5e1" : "#94a3b8"} strokeWidth="2" />
       <circle cx="20" cy="0" r="18" fill={isDarkMode ? "#64748b" : "#334155"} />
       
       {/* 4. Swinging Ball (Right) */}
       <line x1="60" y1="-150" x2="100" y2="-40" stroke={isDarkMode ? "#cbd5e1" : "#94a3b8"} strokeWidth="2" />
       <circle cx="100" cy="-40" r="18" fill={isDarkMode ? "#64748b" : "#334155"} />
    </g>
  </svg>
);

const BreathThumbnail = ({ isDarkMode }: { isDarkMode: boolean }) => (
  <svg viewBox="0 0 300 400" className="w-full h-full" preserveAspectRatio="xMidYMid slice">
    {/* Floating Circles - No Box */}
    <g transform="translate(150, 200)">
       <circle cx="0" cy="0" r="100" fill={isDarkMode ? "#0d9488" : "#2dd4bf"} opacity="0.1" />
       <circle cx="0" cy="0" r="80" fill={isDarkMode ? "#0f766e" : "#14b8a6"} opacity="0.3" />
       <circle cx="0" cy="0" r="60" fill={isDarkMode ? "#115e59" : "#0d9488"} />
    </g>
  </svg>
);

const FireworksThumbnail = ({ isDarkMode }: { isDarkMode: boolean }) => {
  // Reusable burst rays array
  const angles = [0, 45, 90, 135, 180, 225, 270, 315];

  // Helper for the inner content of a single spark
  const SparkContent = () => (
    <>
      {angles.map((deg, i) => (
        <g key={i} transform={`rotate(${deg})`}>
          <line x1="0" y1="-20" x2="0" y2="-70" stroke="#d946ef" strokeWidth="4" strokeLinecap="round" />
          <circle cx="0" cy="-80" r="4" fill="#f0abfc" />
        </g>
      ))}
      <circle cx="0" cy="0" r="6" fill="#fff" opacity="0.8" />
    </>
  );

  // Configuration: Just 3 sparks, positioned carefully within 300x400 bounds
  // Max Radius of a spark at scale 1.0 is roughly 90px
  const sparksConfig = [
    { cx: 150, cy: 200, scale: 1.0 },  // Center Main (Safe zone: 60-240)
    { cx: 230, cy: 100, scale: 0.2 },  // Top Right (Safe zone, radius ~54px)
    { cx: 70, cy: 300, scale: 0.25 },   // Bottom Left (Safe zone, radius ~54px)
  ];

  return (
    <svg viewBox="0 0 300 400" className="w-full h-full" preserveAspectRatio="xMidYMid slice">
      {sparksConfig.map((spark, i) => (
        <g key={i} transform={`translate(${spark.cx}, ${spark.cy}) scale(${spark.scale})`}>
           <SparkContent />
        </g>
      ))}
    </svg>
  );
};

const GravityThumbnail = ({ isDarkMode }: { isDarkMode: boolean }) => (
  <svg viewBox="0 0 300 400" className="w-full h-full" preserveAspectRatio="xMidYMid slice">
    {/* Floating Particles - No Box */}
    <g transform="translate(145, 200)">
       <circle cx="0" cy="0" r="25" fill={isDarkMode ? "#fff" : "#1e40af"} style={{ filter: "drop-shadow(0 0 10px rgba(59, 130, 246, 0.5))" }} />
       {[...Array(12)].map((_, i) => {
          const r = 60 + Math.random() * 40;
          const a = (i / 12) * Math.PI * 2;
          return (
             <circle key={i} cx={Math.cos(a) * r} cy={Math.sin(a) * r} r={4} fill={isDarkMode ? "#60a5fa" : "#2563eb"} />
          )
       })}
       <circle cx="0" cy="0" r="80" fill="none" stroke={isDarkMode ? "#60a5fa" : "#3b82f6"} strokeWidth="1" opacity="0.3" />
    </g>
  </svg>
);

const KaleidoscopeThumbnail = ({ isDarkMode }: { isDarkMode: boolean }) => (
  <svg viewBox="0 0 300 400" className="w-full h-full" preserveAspectRatio="xMidYMid slice">
    {/* Pattern Only - No Box */}
    <g transform="translate(150, 200) scale(2)">
       <g opacity="0.8" style={{ mixBlendMode: isDarkMode ? "screen" : "multiply" }}>
         {[0, 60, 120, 180, 240, 300].map((deg, i) => (
            <g key={i} transform={`rotate(${deg})`}>
               <path d="M0 0 L50 -20 L50 20 Z" fill={["#ec4899", "#06b6d4", "#8b5cf6"][i % 3]} opacity="0.7" />
               <circle cx="45" cy="0" r="4" fill="#fff" opacity="0.6" />
            </g>
         ))}
       </g>
    </g>
  </svg>
);

const RippleThumbnail = ({ isDarkMode }: { isDarkMode: boolean }) => (
  <svg viewBox="0 0 300 400" className="w-full h-full" preserveAspectRatio="xMidYMid slice">
    {/* Floating Rings - No Box */}
    <g transform="translate(150, 200)">
       <circle r="35" stroke={isDarkMode ? "#06b6d4" : "#0891b2"} strokeWidth="4" fill="none" opacity="0.8" />
       <circle r="65" stroke={isDarkMode ? "#22d3ee" : "#06b6d4"} strokeWidth="3" fill="none" opacity="0.6" />
       <circle r="95" stroke={isDarkMode ? "#67e8f9" : "#22d3ee"} strokeWidth="2" fill="none" opacity="0.4" />
    </g>
  </svg>
);

// 2. FIXED VASE (Unified Gradient ID + Crack)
const VaseThumbnail = ({ isDarkMode }: { isDarkMode: boolean }) => (
  <svg viewBox="0 0 300 400" className="w-full h-full" preserveAspectRatio="xMidYMid slice">
    {/* Centered Vase - No Gradient IDs, using Solid Fill for 100% reliability */}
    <g transform="translate(150, 330)">
       {/* Main Body - Solid Terracotta Color */}
       <path d="M -35 0 L 35 0 C 50 -10, 90 -40, 90 -100 C 90 -180, 30 -200, 40 -240 C 50 -250, 50 -260, 0 -260 C -50 -260, -50 -250, -40 -240 C -30 -200, -90 -180, -90 -100 C -90 -40, -50 -10, -35 0 Z" 
             fill="#ea580c" 
             stroke={isDarkMode ? "#431407" : "none"} 
             strokeWidth="1" 
       />
       
       {/* Decoration */}
       <rect x="-40" y="-240" width="80" height="10" fill="#facc15" />
       <ellipse cx="0" cy="-260" rx="35" ry="8" fill="#7c2d12" />
       
       {/* Simple Crack */}
       <g transform="translate(0, -120)">
          <path d="M0 0 L15 10 M0 0 L-10 15 M0 0 L-5 -20 M0 0 L20 -10" stroke="#7c2d12" strokeWidth="2" strokeLinecap="round" />
          <circle cx="0" cy="0" r="3" fill="#7c2d12" />
       </g>
    </g>
  </svg>
);

const PeelThumbnail = ({ isDarkMode }: { isDarkMode: boolean }) => (
  <svg viewBox="0 0 300 400" className="w-full h-full" preserveAspectRatio="xMidYMid slice">
    {/* Floating Peel - No Box */}
    <g transform="translate(50, 50)">
      <rect width="200" height="250" fill={isDarkMode ? "#059669" : "#10b981"} />
      <path d="M200 0 L200 100 L180 90 L160 110 L140 80 L100 0 Z" fill="#fcd34d" style={{ filter: "drop-shadow(2px 2px 5px rgba(0,0,0,0.2))" }} />
      <path d="M200 0 L100 0 L140 80 L160 110 L180 90 L200 100 Z" fill="black" opacity="0.1" />
      <rect x="130" y="150" width="10" height="10" fill="#fcd34d" transform="rotate(15)" />
      <circle cx="150" cy="190" r="5" fill="#fcd34d" />
    </g>
  </svg>
);

const SmileyThumbnail = ({ isDarkMode }: { isDarkMode: boolean }) => (
  <svg viewBox="0 0 300 400" className="w-full h-full" preserveAspectRatio="xMidYMid slice">
    {/* Neutral Face - Centered */}
    <g transform="translate(150, 200)">
       <circle cx="0" cy="0" r="70" fill="#eab308" stroke={isDarkMode ? "none" : "#ca8a04"} strokeWidth="2" />
       <circle cx="-25" cy="-10" r="8" fill="#422006" />
       <circle cx="25" cy="-10" r="8" fill="#422006" />
       <path d="M-20 25 L20 25" stroke="#422006" strokeWidth="4" strokeLinecap="round" />
    </g>
  </svg>
);

const PaperBoatThumbnail = ({ isDarkMode }: { isDarkMode: boolean }) => (
  <svg viewBox="0 0 300 400" className="w-full h-full" preserveAspectRatio="xMidYMid slice">
    {/* REMOVED: <rect> background so it blends with the card color */}
    
    <g transform="translate(150, 200)">
       {/* Ripples - Increased opacity slightly so they are visible on light blue */}
       <circle cx="0" cy="0" r="60" fill="none" stroke="white" strokeWidth="2" opacity="0.5" />
       <circle cx="0" cy="0" r="90" fill="none" stroke="white" strokeWidth="1" opacity="0.3" />
       
       {/* Paper Boat (Top Down View) */}
       <g transform="rotate(-15)">
         {/* Shadow */}
         <path d="M-35 15 L0 50 L35 15 L0 -40 Z" fill="black" opacity="0.1" transform="translate(5, 5)" />
         {/* Boat Body */}
         <path d="M-35 15 L0 50 L35 15 L0 -40 Z" fill="#f8fafc" />
         {/* Fold Lines */}
         <path d="M-35 15 L0 0 L35 15" fill="#e2e8f0" />
         <path d="M0 -40 L0 50" stroke="#cbd5e1" strokeWidth="1" />
       </g>
    </g>
  </svg>
);

const SmileyBackground = ({ isDarkMode }: { isDarkMode: boolean }) => {
  const [smileys, setSmileys] = useState<any[]>([]);
  useEffect(() => {
    const items = Array.from({ length: 15 }).map((_, i) => ({
      id: i, left: Math.random() * 100, top: Math.random() * 100, size: Math.random() * 100 + 40, rotation: Math.random() * 360,
    }));
    setSmileys(items);
  }, []);
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-0 fixed h-full w-full">
      {smileys.map((s) => (
        <div key={s.id} className={`absolute text-transparent stroke-yellow-500 ${isDarkMode ? "opacity-5" : "opacity-10"}`} style={{ left: `${s.left}%`, top: `${s.top}%`, width: `${s.size}px`, height: `${s.size}px`, transform: `rotate(${s.rotation}deg)` }}>
          <Smile className="w-full h-full" strokeWidth={1.5} />
        </div>
      ))}
    </div>
  );
};
// --- Helper: Unified Input Coordinate System ---
const getEventPos = (
  e: MouseEvent | TouchEvent | React.MouseEvent | React.TouchEvent,
  canvas: HTMLCanvasElement | null
) => {
  let clientX = 0;
  let clientY = 0;
  if ("touches" in e && e.touches.length > 0) {
    clientX = e.touches[0].clientX;
    clientY = e.touches[0].clientY;
  } else if ("clientX" in e) {
    clientX = (e as MouseEvent).clientX;
    clientY = (e as MouseEvent).clientY;
  }
  if (canvas) {
    const rect = canvas.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  }
  return { x: clientX, y: clientY };
};

const drawRoundedRect = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) => {
  if (w < 2 * r) r = w / 2;
  if (h < 2 * r) r = h / 2;
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
  ctx.fill();
};

const PebbleMindGame = ({ isDarkMode }: { isDarkMode: boolean }) => {
  // --- Pebble Drag Logic ---
  const [pebblePos, setPebblePos] = useState({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  const handlePointerDown = (e: React.PointerEvent) => {
    e.preventDefault(); e.stopPropagation();
    setIsDragging(true);
    dragOffset.current = { x: e.clientX - pebblePos.x, y: e.clientY - pebblePos.y };
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging) return;
    e.preventDefault(); e.stopPropagation();
    setPebblePos({ x: e.clientX - dragOffset.current.x, y: e.clientY - dragOffset.current.y });
  };

  const handlePointerUp = () => {
    setIsDragging(false);
  };

  // --- Realistic Butterfly Flight Logic ---
  const [flyState, setFlyState] = useState({
    isFlying: false,
    offsetX: 0, offsetY: 0, 
    rotation: 0, scale: 1, facingLeft: false
  });
  
  const flightRef = useRef({
    startTime: 0,
    landingStartTime: 0,
    startLandingPos: { x: 0, y: 0, r: 0, s: 1 }, // Added rotation/scale to start pos
    isLanding: false,
    currentX: 0, currentY: 0,
    prevX: 0, // Track previous X to calculate velocity for banking
  });
  const rafRef = useRef<number>();

  useEffect(() => {
    if (isDragging) {
      // --- START FLYING PHASE ---
      flightRef.current.isLanding = false;
      if (!flightRef.current.startTime) flightRef.current.startTime = Date.now();
      
      const animateFlight = () => {
        const now = Date.now();
        const elapsed = (now - flightRef.current.startTime) / 1000; // Time in seconds

        // 1. WIDER MOVEMENT (360 degree exploration)
        // We mix two sine waves per axis to create "noise" - a path that never repeats cleanly
        const wanderX = Math.sin(elapsed * 1.1) * 160 + Math.cos(elapsed * 2.3) * 90;
        const wanderY = -150 + Math.sin(elapsed * 1.4) * 120 + Math.cos(elapsed * 0.8) * 60;

        // Smoothly interpolate current pos to target (Ease-out feel)
        flightRef.current.currentX += (wanderX - flightRef.current.currentX) * 0.05;
        flightRef.current.currentY += (wanderY - flightRef.current.currentY) * 0.05;

        const currX = flightRef.current.currentX;
        const currY = flightRef.current.currentY;

        // 2. ROTATION (Banking + Swaying)
        // Calculate velocity (how fast did we move horizontally?)
        const velocityX = currX - flightRef.current.prevX;
        flightRef.current.prevX = currX; // Store for next frame

        // "Bank" into the turn: if moving left, tilt left. 
        const bankAngle = velocityX * 3.5; 
        // "Sway" randomly: simulate air turbulence
        const randomSway = Math.sin(elapsed * 2.5) * 15; 
        
        const rotation = bankAngle + randomSway;

        // 3. DEPTH ILLUSION (Random Scale)
        // Oscillate scale to simulate flying towards/away from the camera (Z-axis)
        const zDepth = Math.sin(elapsed * 0.9); // Goes from -1 to 1
        const scale = 1.1 + (zDepth * 0.3); // Scale fluctuates between 0.8 and 1.4

        // 4. FACING DIRECTION (Flip)
        // Only change facing if moving significantly to avoid jitter
        const facingLeft = velocityX < -0.5 ? true : (velocityX > 0.5 ? false : flyState.facingLeft);

        setFlyState({ isFlying: true, offsetX: currX, offsetY: currY, rotation, scale, facingLeft });
        rafRef.current = requestAnimationFrame(animateFlight);
      };
      rafRef.current = requestAnimationFrame(animateFlight);

    } else if (flyState.isFlying && !flightRef.current.isLanding) {
      // --- START LANDING PHASE ---
      flightRef.current.isLanding = true;
      flightRef.current.landingStartTime = Date.now();
      flightRef.current.startLandingPos = { 
        x: flightRef.current.currentX, 
        y: flightRef.current.currentY,
        r: flyState.rotation,
        s: flyState.scale
      };

      const animateLanding = () => {
        const now = Date.now();
        const landingDuration = 600; // Fast landing
        const progress = Math.min(1, (now - flightRef.current.landingStartTime) / landingDuration);
        const ease = 1 - Math.pow(1 - progress, 3); // Cubic Ease Out

        // Interpolate Position back to 0,0
        const currX = flightRef.current.startLandingPos.x * (1 - ease);
        const currY = flightRef.current.startLandingPos.y * (1 - ease);
        
        // Interpolate Rotation/Scale back to "sitting" state (0 deg, 1.0 scale)
        const rotation = flightRef.current.startLandingPos.r * (1 - ease);
        const scale = flightRef.current.startLandingPos.s + (1 - flightRef.current.startLandingPos.s) * ease;

        // Update refs for continuity
        flightRef.current.currentX = currX;
        flightRef.current.currentY = currY;
        flightRef.current.prevX = currX;

        setFlyState(s => ({ ...s, offsetX: currX, offsetY: currY, rotation, scale }));

        if (progress < 1) {
          rafRef.current = requestAnimationFrame(animateLanding);
        } else {
          // Reset State on Completion
          setFlyState({ isFlying: false, offsetX: 0, offsetY: 0, rotation: 0, scale: 1, facingLeft: false });
          flightRef.current.isLanding = false;
          flightRef.current.startTime = 0;
        }
      };
      rafRef.current = requestAnimationFrame(animateLanding);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isDragging]); 


  // Combine pebble position with the flight offsets
  const butterflyTransform = `translate(${pebblePos.x + flyState.offsetX}px, ${pebblePos.y + flyState.offsetY}px) scale(${flyState.scale}) rotate(${flyState.rotation}deg)`;

  return (
    <div 
      className="w-full h-full relative overflow-hidden touch-none select-none cursor-move"
      onPointerMove={handlePointerMove} onPointerUp={handlePointerUp} onPointerLeave={handlePointerUp}
    >
      <style>{`
        @keyframes wingFlapSlow { 0%, 100% { transform: scaleX(1); } 50% { transform: scaleX(0.7); } }
        @keyframes wingFlapFast { 0%, 100% { transform: scaleX(1); } 50% { transform: scaleX(0.25); } }
        .flap-slow { animation: wingFlapSlow 3s infinite ease-in-out; transform-origin: 50px 50px; }
        .flap-fast { animation: wingFlapFast 0.15s infinite ease-in-out; transform-origin: 50px 50px; }
      `}</style>

      {/* --- The Pebble --- */}
      <div
        onPointerDown={handlePointerDown}
        style={{ transform: `translate(${pebblePos.x}px, ${pebblePos.y}px)`, touchAction: 'none', willChange: 'transform' }}
        className="absolute top-0 left-0 -ml-16 -mt-10 w-32 h-20 z-10"
      >
        <svg viewBox="-60 -40 120 100" className="w-full h-full overflow-visible">
           <ellipse cx="0" cy="35" rx="45" ry="8" fill="black" opacity={isDragging ? "0.1" : "0.2"} className="transition-opacity duration-300"/>
           <path d="M-50 10 Q-60 -20 0 -30 Q60 -20 50 10 Q0 40 -50 10" fill={isDarkMode ? "#57534e" : "#a8a29e"} stroke={isDarkMode ? "#292524" : "#78716c"} strokeWidth="3" className="drop-shadow-sm active:scale-95 transition-transform origin-center"/>
           <path d="M-30 -10 Q-10 -15 10 -10" fill="none" stroke="white" strokeWidth="2" opacity="0.2" strokeLinecap="round"/>
        </svg>
      </div>

      {/* --- The Butterfly --- */}
      <div className="absolute top-0 left-0 -ml-12 -mt-16 w-24 h-24 pointer-events-none z-20"
        style={{ 
            transform: butterflyTransform, 
            willChange: 'transform' // Essential for smooth performance
        }}
      >
        <div className="w-full h-full transition-transform duration-300" style={{ transform: flyState.facingLeft ? 'scaleX(-1)' : 'scaleX(1)' }}>
            <svg viewBox="0 0 100 100" className="w-full h-full">
            <g className={flyState.isFlying ? 'flap-fast' : 'flap-slow'} style={{ transformOrigin: '50px 50px' }}>
                <path d="M50 50 C 20 20, 0 10, 10 50 C 0 80, 20 80, 50 50" fill="#FBBF24" stroke="#D97706" strokeWidth="1.5" />
                <path d="M50 50 C 80 20, 100 10, 90 50 C 100 80, 80 80, 50 50" fill="#FBBF24" stroke="#D97706" strokeWidth="1.5" />
            </g>
            <ellipse cx="50" cy="50" rx="3" ry="15" fill="#451a03" />
            <path d="M50 38 L 42 25" stroke="#451a03" strokeWidth="1" opacity="0.8"/>
            <path d="M50 38 L 58 25" stroke="#451a03" strokeWidth="1" opacity="0.8"/>
            </svg>
        </div>
      </div>

       {/* --- Bottom Instruction Line --- */}
       <div className={`absolute bottom-16 w-full text-center pointer-events-none font-medium text-sm uppercase tracking-wider animate-pulse ${isDarkMode ? "text-stone-600" : "text-stone-400"}`}>Drag the stone</div>
    </div>
  );
};

const TrampolineGame = ({ isDarkMode }: { isDarkMode: boolean }) => {
  const cvs = useRef<HTMLCanvasElement>(null);
  const state = useRef({ ball: { x: 0, y: 0, vy: 0, r: 40, squash: 1, resting: true }, tramp: { deform: 0 }, isReady: false });
  useEffect(() => {
    const c = cvs.current; if (!c) return; const ctx = c.getContext("2d"); if (!ctx) return; let f: number;
    const resize = () => { c.width = window.innerWidth; c.height = window.innerHeight; if (!state.current.isReady) { state.current.ball.x = c.width / 2; const floorY = c.height * 0.8; const trampY = floorY - 50; state.current.ball.y = trampY - 40; state.current.isReady = true; } };
    resize(); window.addEventListener("resize", resize);
    const render = () => {
      const { width: w, height: h } = c; const s = state.current; const floorY = h * 0.8; const gravity = 0.5; const bounce = -0.75; const trampY = floorY - 50;
      if (s.ball.resting) { s.ball.vy = 0; s.ball.y = trampY - s.ball.r; s.tramp.deform = s.tramp.deform * 0.8; } else { s.ball.vy += gravity; s.ball.y += s.ball.vy; }
      if (!s.ball.resting && s.ball.y + s.ball.r > trampY) { const penetration = s.ball.y + s.ball.r - trampY; s.tramp.deform = penetration; if (s.ball.vy > 0) { if (penetration > 15) { if (s.ball.vy < 4) { s.ball.resting = true; } else { s.ball.vy *= bounce; s.ball.y = trampY - s.ball.r + penetration; } } } } else { s.tramp.deform *= 0.8; }
      const speed = Math.abs(s.ball.vy); const targetSquash = 1 + speed * 0.01 * (s.ball.vy < 0 ? 1 : -0.5); s.ball.squash += (targetSquash - s.ball.squash) * 0.1;
      ctx.fillStyle = isDarkMode ? "#1e3a8a" : "#4f86f7"; ctx.fillRect(0, 0, w, h); const cx = w / 2;
      ctx.strokeStyle = isDarkMode ? "#94a3b8" : "#7dd3fc"; ctx.lineWidth = 8; ctx.lineCap = "round"; ctx.beginPath(); ctx.moveTo(cx - 120, trampY); ctx.lineTo(cx - 120, trampY + 60); ctx.stroke(); ctx.beginPath(); ctx.moveTo(cx + 120, trampY); ctx.lineTo(cx + 120, trampY + 60); ctx.stroke(); ctx.beginPath(); ctx.moveTo(cx - 60, trampY + 10); ctx.lineTo(cx - 60, trampY + 50); ctx.stroke(); ctx.beginPath(); ctx.moveTo(cx + 60, trampY + 10); ctx.lineTo(cx + 60, trampY + 50); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(cx - 150, trampY); ctx.quadraticCurveTo(cx, trampY + s.tramp.deform * 1.5, cx + 150, trampY); ctx.strokeStyle = isDarkMode ? "#cbd5e1" : "#bae6fd"; ctx.lineWidth = 12; ctx.stroke(); ctx.fillStyle = "rgba(255,255,255,0.9)"; ctx.fill();
      ctx.save(); ctx.translate(s.ball.x, s.ball.y); ctx.scale(1 / s.ball.squash, s.ball.squash); ctx.fillStyle = "#fde047"; ctx.beginPath(); ctx.arc(0, 0, s.ball.r, 0, Math.PI * 2); ctx.fill(); ctx.fillStyle = "#1e293b"; ctx.beginPath(); ctx.arc(-15, -5, 6, 0, Math.PI * 2); ctx.fill(); ctx.beginPath(); ctx.arc(15, -5, 6, 0, Math.PI * 2); ctx.fill(); ctx.fillStyle = "#ef4444"; ctx.beginPath(); ctx.arc(0, 10, 8, 0, Math.PI, false); ctx.fill(); ctx.strokeStyle = "#fde047"; ctx.lineWidth = 8; ctx.lineCap = "round"; const armAngle = s.ball.vy < 0 ? -0.5 : 0.5; ctx.beginPath(); ctx.moveTo(-35, 0); ctx.lineTo(-55, 10 * armAngle - 10); ctx.stroke(); ctx.beginPath(); ctx.moveTo(35, 0); ctx.lineTo(55, 10 * armAngle - 10); ctx.stroke(); const legWiggle = Math.sin(Date.now() / 50) * 5; ctx.beginPath(); ctx.moveTo(-15, 35); ctx.lineTo(-20 + legWiggle, 50); ctx.stroke(); ctx.beginPath(); ctx.moveTo(15, 35); ctx.lineTo(20 - legWiggle, 50); ctx.stroke(); ctx.restore();
      f = requestAnimationFrame(render);
    };
    render();
    const jump = (e: any) => { e.preventDefault(); state.current.ball.resting = false; state.current.ball.vy = -18; state.current.ball.squash = 0.8; if (navigator.vibrate) navigator.vibrate(10); };
    c.addEventListener("mousedown", jump); c.addEventListener("touchstart", jump, { passive: false });
    return () => { window.removeEventListener("resize", resize); c.removeEventListener("mousedown", jump); c.removeEventListener("touchstart", jump); cancelAnimationFrame(f); };
  }, [isDarkMode]);
  return <div className="w-full h-full relative"><canvas ref={cvs} className="w-full h-full cursor-pointer touch-none" /><div className="absolute bottom-20 w-full text-center pointer-events-none text-white/70 font-bold text-xl animate-pulse">Tap to Jump!</div></div>;
};

const DuckPondGame = ({ isDarkMode }: { isDarkMode: boolean }) => {
  const cvs = useRef<HTMLCanvasElement>(null);
  const state = useRef({ duck: { x: 0, y: 0, tx: 0, ty: 0, a: 0, speed: 0 }, ripples: [] as any[], pads: [] as any[] });
  useEffect(() => {
    const c = cvs.current; if (!c) return; const ctx = c.getContext("2d"); if (!ctx) return; let f: number;
    const resize = () => { c.width = window.innerWidth; c.height = window.innerHeight; state.current.duck.x = c.width / 2; state.current.duck.y = c.height / 2; state.current.duck.tx = c.width / 2; state.current.duck.ty = c.height / 2; state.current.pads = Array(8).fill(0).map(() => ({ x: Math.random() * c.width, y: Math.random() * c.height, r: 14 + Math.random() * 20, angle: Math.random() * Math.PI * 2 })); };
    resize(); window.addEventListener("resize", resize);
    const render = () => {
      const { width: w, height: h } = c; const s = state.current; const d = s.duck;
      if (isDarkMode) { const g = ctx.createLinearGradient(0, 0, 0, h); g.addColorStop(0, "#0f172a"); g.addColorStop(1, "#1e3a8a"); ctx.fillStyle = g; } else { const g = ctx.createRadialGradient(w / 2, h / 2, 100, w / 2, h / 2, Math.max(w, h)); g.addColorStop(0, "#60a5fa"); g.addColorStop(1, "#3b82f6"); ctx.fillStyle = g; }
      ctx.fillRect(0, 0, w, h); ctx.globalAlpha = 0.5; s.pads.forEach((p) => { ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.angle); ctx.fillStyle = isDarkMode ? "#14532d" : "#4ade80"; ctx.strokeStyle = isDarkMode ? "#052e16" : "#166534"; ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(0, 0, p.r, 0.2, Math.PI * 2 - 0.2); ctx.lineTo(0, 0); ctx.closePath(); ctx.fill(); ctx.stroke(); ctx.restore(); }); ctx.globalAlpha = 1.0;
      const dx = d.tx - d.x; const dy = d.ty - d.y; const dist = Math.hypot(dx, dy);
      if (dist > 5) { const angle = Math.atan2(dy, dx) + Math.PI / 2; let diff = angle - d.a; while (diff < -Math.PI) diff += Math.PI * 2; while (diff > Math.PI) diff -= Math.PI * 2; d.a += diff * 0.1; d.speed = Math.min(dist * 0.05, 4); d.x += Math.cos(d.a - Math.PI / 2) * d.speed; d.y += Math.sin(d.a - Math.PI / 2) * d.speed; if (Math.random() < 0.1) { s.ripples.push({ x: d.x, y: d.y, r: 10, op: 0.5 }); } } else { d.speed = 0; d.x += Math.sin(Date.now() / 1000) * 0.2; d.y += Math.cos(Date.now() / 1500) * 0.2; }
      for (let i = s.ripples.length - 1; i >= 0; i--) { const r = s.ripples[i]; r.r += 0.5; r.op -= 0.01; if (r.op <= 0) { s.ripples.splice(i, 1); } else { ctx.beginPath(); ctx.arc(r.x, r.y, r.r, 0, Math.PI * 2); ctx.strokeStyle = `rgba(255, 255, 255, ${r.op})`; ctx.stroke(); } }
      ctx.save(); ctx.translate(d.x, d.y); ctx.rotate(d.a); ctx.scale(0.12, 0.12); ctx.translate(-300, -500); ctx.fillStyle = "#e9a417"; ctx.beginPath(); ctx.ellipse(110, 520, 90, 200, 0, 0, Math.PI * 2); ctx.fill(); ctx.beginPath(); ctx.ellipse(490, 520, 90, 200, 0, 0, Math.PI * 2); ctx.fill(); ctx.fillStyle = "#f7d83a"; ctx.beginPath(); ctx.ellipse(300, 760, 150, 150, 0, 0, Math.PI * 2); ctx.fill(); ctx.fillStyle = "#f7c623"; ctx.beginPath(); ctx.ellipse(300, 520, 240, 330, 0, 0, Math.PI * 2); ctx.fill(); ctx.fillStyle = "#f9d939"; ctx.beginPath(); ctx.ellipse(300, 260, 180, 160, 0, 0, Math.PI * 2); ctx.fill(); ctx.fillStyle = "#df6724"; ctx.beginPath(); ctx.moveTo(240, 90); ctx.quadraticCurveTo(300, -20, 360, 90); ctx.quadraticCurveTo(360, 180, 300, 150); ctx.quadraticCurveTo(240, 180, 240, 90); ctx.fill(); ctx.fillStyle = "rgba(233, 130, 60, 0.8)"; ctx.beginPath(); ctx.moveTo(260, 100); ctx.quadraticCurveTo(300, 20, 340, 100); ctx.quadraticCurveTo(340, 150, 300, 130); ctx.quadraticCurveTo(260, 150, 260, 100); ctx.fill(); ctx.fillStyle = "#3a3a3a"; ctx.beginPath(); ctx.ellipse(210, 260, 30, 40, 0, 0, Math.PI * 2); ctx.fill(); ctx.beginPath(); ctx.ellipse(390, 260, 30, 40, 0, 0, Math.PI * 2); ctx.fill(); ctx.fillStyle = "#ffffff"; ctx.beginPath(); ctx.arc(220, 250, 8, 0, Math.PI * 2); ctx.fill(); ctx.beginPath(); ctx.arc(400, 250, 8, 0, Math.PI * 2); ctx.fill(); ctx.restore();
      if (d.speed > 0.5) { ctx.beginPath(); ctx.arc(d.tx, d.ty, 5 + Math.sin(Date.now() / 200) * 2, 0, Math.PI * 2); ctx.strokeStyle = "rgba(255,255,255,0.3)"; ctx.stroke(); }
      f = requestAnimationFrame(render);
    };
    render();
    const handleInput = (e: any) => { const p = getEventPos(e, c); state.current.duck.tx = p.x; state.current.duck.ty = p.y; for (let i = 0; i < 3; i++) { state.current.ripples.push({ x: p.x, y: p.y, r: 5, op: 1 }); } };
    c.addEventListener("mousedown", handleInput); c.addEventListener("touchstart", (e) => { e.preventDefault(); handleInput(e); }, { passive: false }); c.addEventListener("touchmove", (e) => { e.preventDefault(); handleInput(e); }, { passive: false });
    return () => { window.removeEventListener("resize", resize); cancelAnimationFrame(f); };
  }, [isDarkMode]);
  return <div className="w-full h-full relative"><canvas ref={cvs} className="w-full h-full cursor-pointer touch-none" /><div className="absolute bottom-10 w-full text-center pointer-events-none text-white/50 font-medium">Tap anywhere to swim</div></div>;
};

const BubblePopGame = ({ isDarkMode }: { isDarkMode: boolean }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = canvasRef.current; if (!c) return; const ctx = c.getContext('2d'); if (!ctx) return; let f: number; let b: any[] = []; let parts: any[] = [];
    const resize = () => { c.width = window.innerWidth; c.height = window.innerHeight; }; resize(); window.addEventListener('resize', resize);
    for (let i = 0; i < 20; i++) { b.push({ x: Math.random() * c.width, y: Math.random() * c.height, r: Math.random() * 25 + 20, vy: Math.random() * 0.5 + 0.5, c: `hsla(${Math.random() * 360}, 70%, 60%, 0.4)` }); }
    const render = () => {
      f = requestAnimationFrame(render); const grad = ctx.createLinearGradient(0, 0, 0, c.height); if (isDarkMode) { grad.addColorStop(0, '#0f172a'); grad.addColorStop(1, '#1e293b'); } else { grad.addColorStop(0, '#60a5fa'); grad.addColorStop(1, '#dbeafe'); } ctx.fillStyle = grad; ctx.fillRect(0, 0, c.width, c.height);
      if (Math.random() < 0.03) { b.push({ x: Math.random() * c.width, y: c.height + 30, r: Math.random() * 25 + 20, vy: Math.random() * 0.5 + 0.5, c: `hsla(${Math.random() * 360}, 70%, 60%, 0.4)` }); }
      b.forEach((bub, i) => { bub.y -= bub.vy; bub.x += Math.sin(bub.y * 0.01) * 0.5; ctx.beginPath(); ctx.arc(bub.x, bub.y, bub.r, 0, 7); ctx.fillStyle = bub.c; ctx.fill(); ctx.strokeStyle = 'rgba(255,255,255,0.8)'; ctx.lineWidth = 2; ctx.stroke(); ctx.beginPath(); ctx.arc(bub.x - bub.r * 0.3, bub.y - bub.r * 0.3, bub.r * 0.2, 0, 7); ctx.fillStyle = 'rgba(255,255,255,0.6)'; ctx.fill(); if (bub.y < -50) b.splice(i, 1); });
      parts.forEach((p, i) => { p.y += p.vy; p.x += p.vx; p.l -= 0.05; ctx.fillStyle = `rgba(255,255,255,${p.l})`; ctx.beginPath(); ctx.arc(p.x, p.y, 2, 0, 7); ctx.fill(); if (p.l <= 0) parts.splice(i, 1); });
    };
    render();
    const touch = (e: any) => {
      e.preventDefault(); const pos = getEventPos(e, c); for (let i = b.length - 1; i >= 0; i--) { if (Math.hypot(b[i].x - pos.x, b[i].y - pos.y) < b[i].r + 20) { for (let k = 0; k < 5; k++) parts.push({ x: b[i].x, y: b[i].y, vx: (Math.random() - 0.5) * 5, vy: (Math.random() - 0.5) * 5, l: 1 }); b.splice(i, 1); if (navigator.vibrate) navigator.vibrate(5); break; } }
    };
    c.addEventListener('mousedown', touch); c.addEventListener('touchstart', touch, { passive: false });
    return () => { window.removeEventListener('resize', resize); c.removeEventListener('mousedown', touch); c.removeEventListener('touchstart', touch); cancelAnimationFrame(f); };
  }, [isDarkMode]);
  return <canvas ref={canvasRef} className="w-full h-full cursor-crosshair touch-none" />;
};

const FidgetSpinnerGame = ({ isDarkMode }: { isDarkMode: boolean }) => {
  const [rot, setRot] = useState(0); const vel = useRef(0); const drag = useRef(false); const last = useRef(0); const req = useRef<number>();
  const getA = (cx: number, cy: number, ex: number, ey: number) => Math.atan2(ey - cy, ex - cx) * 180 / Math.PI;
  const move = (cx: number, cy: number) => { if (!drag.current) return; const ang = getA(window.innerWidth / 2, window.innerHeight / 2, cx, cy); let d = ang - last.current; if (d > 180) d -= 360; if (d < -180) d += 360; setRot(r => r + d); vel.current = d; last.current = ang; };
  useEffect(() => { const loop = () => { if (!drag.current && Math.abs(vel.current) > 0.1) { setRot(r => r + vel.current); vel.current *= 0.985; } req.current = requestAnimationFrame(loop); }; req.current = requestAnimationFrame(loop); return () => cancelAnimationFrame(req.current!); }, []);
  return (
    <div className={`w-full h-full flex items-center justify-center touch-none select-none ${isDarkMode ? 'bg-slate-900' : 'bg-[#FFF8F0]'}`} onMouseDown={e => { drag.current = true; last.current = getA(window.innerWidth / 2, window.innerHeight / 2, e.clientX, e.clientY); }} onMouseMove={e => move(e.clientX, e.clientY)} onMouseUp={() => drag.current = false} onTouchStart={e => { drag.current = true; last.current = getA(window.innerWidth / 2, window.innerHeight / 2, e.touches[0].clientX, e.touches[0].clientY); }} onTouchMove={e => move(e.touches[0].clientX, e.touches[0].clientY)} onTouchEnd={() => drag.current = false}>
      <div style={{ transform: `rotate(${rot}deg)` }} className="w-72 h-72 drop-shadow-2xl"><svg viewBox="0 0 100 100"><g fill="#f43f5e"><circle cx="50" cy="15" r="12" /><circle cx="50" cy="15" r="7" fill="#333" /></g><g fill="#22c55e"><circle cx="19.7" cy="67.5" r="12" /><circle cx="19.7" cy="67.5" r="7" fill="#333" /></g><g fill="#3b82f6"><circle cx="80.3" cy="67.5" r="12" /><circle cx="80.3" cy="67.5" r="7" fill="#333" /></g><path d="M50 50 L50 27 Q50 50 32 58 L50 50 L68 58 Q50 50 50 27 Z" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="10" strokeLinecap="round" strokeLinejoin="round" /><circle cx="50" cy="50" r="14" fill="#fff" stroke="#64748b" strokeWidth="2" /></svg></div>
    </div>
  );
};

const StoneSkipGame = ({ isDarkMode }: { isDarkMode: boolean }) => {
  const cvs = useRef<HTMLCanvasElement>(null);
  // Physics state: 
  // s = stone (x,y,z)
  // rips = ripples on water
  // parts = water droplets (particles)
  const dat = useRef({ 
    ph: 'aim', 
    ax: 0, ay: 0, 
    dx: 0, dy: 0, 
    dragging: false, 
    s: { x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, rot: 0 }, 
    rips: [] as any[],
    parts: [] as any[] // Splash particles
  });

  useEffect(() => {
    const c = cvs.current; if (!c) return; const ctx = c.getContext('2d'); if (!ctx) return; 
    let f: number;

    const rsz = () => { 
      c.width = window.innerWidth; 
      c.height = window.innerHeight; 
      dat.current.ax = c.width / 2; 
      dat.current.ay = c.height - 150; 
      dat.current.dx = dat.current.ax; 
      dat.current.dy = dat.current.ay;
    }; 
    rsz(); 
    window.addEventListener('resize', rsz);

    const rnd = () => {
      const { width: w, height: h } = c; 
      const s = dat.current.s; 
      const d = dat.current;

      // 1. Draw Water (Full Screen)
      const wat = ctx.createLinearGradient(0, 0, 0, h);
      if (isDarkMode) { 
        wat.addColorStop(0, '#0f172a'); 
        wat.addColorStop(1, '#1e3a8a'); 
      } else { 
        wat.addColorStop(0, '#0ea5e9'); 
        wat.addColorStop(1, '#3b82f6'); 
      } 
      ctx.fillStyle = wat; 
      ctx.fillRect(0, 0, w, h);

      // 2. Update & Draw Ripples
      for (let i = d.rips.length - 1; i >= 0; i--) {
        const r = d.rips[i];
        r.age += 1;
        // Mini ripples grow slower than main ones
        const growth = r.type === 'mini' ? 0.8 : 1.5; 
        r.r += growth; 
        const fadeSpeed = r.type === 'mini' ? 50 : 80;
        const alpha = Math.max(0, (fadeSpeed - r.age) / fadeSpeed);
        
        ctx.beginPath();
        // Flatten eclipse to look like perspective water
        ctx.ellipse(r.x, r.y, r.r * 2, r.r * 0.6, 0, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(255,255,255,${alpha * 0.5})`;
        ctx.lineWidth = r.type === 'mini' ? 1 : 2;
        ctx.stroke();
        
        if (alpha <= 0) d.rips.splice(i, 1);
      }

      // 3. Update & Draw Splash Particles (Droplets)
      for (let i = d.parts.length - 1; i >= 0; i--) {
        const p = d.parts[i];
        // Move
        p.x += p.vx;
        p.y += p.vy;
        p.z += p.vz;
        p.vz -= 0.3; // Heavy gravity for water drops
        
        // Draw Droplet
        if (p.z > 0) {
            ctx.fillStyle = isDarkMode ? '#94a3b8' : '#e0f2fe';
            ctx.beginPath();
            ctx.arc(p.x, p.y - p.z, p.size, 0, Math.PI * 2);
            ctx.fill();
        }

        // Droplet hits water -> Create Mini Ripple
        if (p.z <= 0) {
            d.rips.push({ x: p.x, y: p.y, r: 1, age: 0, type: 'mini' });
            d.parts.splice(i, 1);
        }
      }

      // 4. Aiming Visuals
      if (d.ph === 'aim') {
        ctx.beginPath();
        ctx.moveTo(d.ax, d.ay);
        ctx.lineTo(d.dx, d.dy);
        ctx.strokeStyle = 'rgba(255,255,255,0.3)';
        ctx.setLineDash([5, 5]);
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.setLineDash([]);

        // Handle
        ctx.beginPath();
        ctx.arc(d.dx, d.dy, 15, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255,255,255,0.2)';
        ctx.fill();
        ctx.strokeStyle = 'white';
        ctx.stroke();

        // Stone
        ctx.beginPath();
        ctx.ellipse(d.ax, d.ay, 20, 12, 0, 0, Math.PI * 2);
        ctx.fillStyle = isDarkMode ? '#94a3b8' : '#cbd5e1';
        ctx.fill();
      }

      // 5. Stone Physics
      if (d.ph === 'fly' || d.ph === 'sink') {
        s.x += s.vx; s.y += s.vy; s.z += s.vz; 
        s.vz -= 0.35; 
        s.vx *= 0.995; s.vy *= 0.995; 

        // Water Impact
        if (s.z <= 0) { 
            const speed = Math.hypot(s.vx, s.vy);
            
            // Skip Logic
            if (d.ph !== 'sink' && speed > 2.0 && s.vz > -8) {
                s.z = 0; 
                s.vz = Math.abs(s.vz) * 0.65; 
                s.vx *= 0.9; s.vy *= 0.9; 
                
                // 1. Create Main Ripple
                d.rips.push({ x: s.x, y: s.y, r: 5, age: 0, type: 'main' });

                // 2. Create Splash Drops (Particles)
                const splashCount = Math.floor(speed * 1.5) + 3; // More speed = more drops
                for (let k = 0; k < splashCount; k++) {
                    d.parts.push({
                        x: s.x, 
                        y: s.y, 
                        z: 0,
                        // Forward momentum + randomness
                        vx: s.vx * 0.5 + (Math.random() - 0.5) * 4, 
                        vy: s.vy * 0.5 + (Math.random() - 0.5) * 4,
                        vz: Math.random() * 4 + 2, // Pop up
                        size: Math.random() * 2 + 1
                    });
                }

                if (navigator.vibrate) navigator.vibrate(10);
            } else {
                // Sink Logic
                d.ph = 'sink';
                s.vx *= 0.8; s.vy *= 0.8; 
                s.vz -= 0.1; 
                if (s.z > -5) d.rips.push({ x: s.x, y: s.y, r: 2, age: 0, type: 'main' });
            }
        }

        // Reset
        if (s.x < -50 || s.x > w + 50 || s.y < -50 || s.y > h + 50 || s.z < -100) {
           d.ph = 'aim'; d.dx = d.ax; d.dy = d.ay;
        }

        // Shadow
        if (s.z > 0) {
            ctx.fillStyle = 'rgba(0,0,0,0.2)';
            ctx.beginPath();
            const shadowSize = Math.max(0, 20 - s.z/10);
            ctx.ellipse(s.x, s.y, shadowSize, shadowSize * 0.6, 0, 0, Math.PI * 2);
            ctx.fill();
        }

        // Stone (Steady, no spin)
        ctx.save();
        ctx.translate(s.x, s.y - s.z); 
        ctx.fillStyle = isDarkMode ? '#94a3b8' : '#e2e8f0';
        ctx.beginPath();
        ctx.ellipse(0, 0, 20, 12, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = 'rgba(255,255,255,0.3)';
        ctx.beginPath();
        ctx.ellipse(-5, -3, 8, 4, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }

      f = requestAnimationFrame(rnd);
    }; 
    rnd();

    const hS = (e: any) => { 
        const p = getEventPos(e, c); 
        if (dat.current.ph === 'aim' && Math.hypot(p.x - dat.current.ax, p.y - dat.current.ay) < 100) {
            dat.current.dragging = true; 
            dat.current.dx = p.x; dat.current.dy = p.y; 
        }
    };
    
    const hM = (e: any) => { 
        if (!dat.current.dragging) return;
        const p = getEventPos(e, c); 
        const maxDrag = 150;
        const dx = p.x - dat.current.ax;
        const dy = p.y - dat.current.ay;
        const dist = Math.hypot(dx, dy);
        
        if (dist > maxDrag) {
            const angle = Math.atan2(dy, dx);
            dat.current.dx = dat.current.ax + Math.cos(angle) * maxDrag;
            dat.current.dy = dat.current.ay + Math.sin(angle) * maxDrag;
        } else {
            dat.current.dx = p.x; dat.current.dy = p.y;
        }
    };

    const hE = () => { 
        if (dat.current.dragging) { 
            dat.current.ph = 'fly'; 
            const pwr = 0.15;
            const vx = (dat.current.ax - dat.current.dx) * pwr;
            const vy = (dat.current.ay - dat.current.dy) * pwr;
            dat.current.s = { x: dat.current.ax, y: dat.current.ay, z: 10, vx: vx, vy: vy, vz: 4, rot: 0 }; 
        } 
        dat.current.dragging = false; 
    };

    c.addEventListener('mousedown', hS); c.addEventListener('mousemove', hM);
    window.addEventListener('mouseup', hE); 
    c.addEventListener('touchstart', hS, { passive: false });
    c.addEventListener('touchmove', hM, { passive: false });
    window.addEventListener('touchend', hE);

    return () => { 
        window.removeEventListener('resize', rsz); cancelAnimationFrame(f); 
        c.removeEventListener('mousedown', hS); c.removeEventListener('mousemove', hM);
        window.removeEventListener('mouseup', hE); c.removeEventListener('touchstart', hS);
        c.removeEventListener('touchmove', hM); window.removeEventListener('touchend', hE);
    };
  }, [isDarkMode]);

  return (
      <div className="w-full h-full relative touch-none select-none">
          <canvas ref={cvs} className="w-full h-full cursor-move block" />
          <div className={`absolute bottom-20 w-full text-center pointer-events-none font-bold text-sm uppercase tracking-widest opacity-50 ${isDarkMode ? "text-blue-200" : "text-blue-900"}`}>
             Pull back & Release
          </div>
      </div>
  );
};
const PunchingBagGame = ({ isDarkMode }: { isDarkMode: boolean }) => {
  const cvs = useRef<HTMLCanvasElement>(null);
  const splashes = useRef<any[]>([]);

  useEffect(() => {
    const c = cvs.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;

    // --- 1. PREPARE THE SVG IMAGE ---
    const svgString = `
      <svg id="Layer_1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 35.06 27.7">
        <defs><style>.cls-1{fill:#fb565a;}.cls-2{fill:#ea3946;}.cls-3{fill:#434a5c;}</style></defs>
        <path class="cls-2" d="M23.43,5.02c-.09-2.28-1.3-4.14-3.27-4.88-3.65-1.36-9.13,7.52-9.13,7.52l4.53,5.9s8.03-4.48,7.87-8.55Z"/>
        <path class="cls-3" d="M11.79,26.26l-9.09.14c-.67.01-1.28-.43-1.46-1.07C-.32,19.67-.41,13.99.99,8.28c.16-.65.76-1.11,1.43-1.12l9.09-.14c1.88,6.96,2.14,13.42.29,19.24Z"/>
        <path class="cls-1" d="M11.79,26.27c-1.87-6.39-1.97-12.8-.29-19.24,0,0,17.12-3.94,20.86-.31,3.45,3.35,3.64,15.79.29,19.24-3.63,3.74-20.86.31-20.86.31Z"/>
      </svg>`;
    
    const gloveImg = new Image();
    const blob = new Blob([svgString], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    gloveImg.src = url;

    // --- GAME LOGIC ---
    let f: number;
    const p = { a: 0, v: 0, l: { x: -180, s: 'i' }, r: { x: 180, s: 'i' } };

    const drawRoundedRect = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) => {
      ctx.beginPath(); ctx.roundRect(x, y, w, h, r); ctx.fill();
    };
    
    // Helper to get touch/mouse position relative to canvas
    const getEventPos = (e: any, canvas: HTMLCanvasElement) => {
        const rect = canvas.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        return { x: clientX - rect.left, y: clientY - rect.top };
    };

    const rsz = () => { c.width = window.innerWidth; c.height = window.innerHeight; };
    rsz();
    window.addEventListener('resize', rsz);

    const render = () => {
      const { width: w, height: h } = c;
      const cx = w / 2;
      const cy = h / 4;

      // Background
      ctx.fillStyle = isDarkMode ? '#1e293b' : '#e2e8f0';
      ctx.fillRect(0, 0, w, h);

      // Physics
      p.v += -0.015 * Math.sin(p.a);
      p.v *= 0.96;
      p.a += p.v;
      const upG = (g: any, ix: number, hx: number) => {
        if (g.s === 'p') {
          g.x += (hx - g.x) * 0.4;
          if (Math.abs(g.x - hx) < 10) g.s = 'r';
        } else {
          g.x += (ix - g.x) * 0.1;
        }
      };
      upG(p.l, -160, -60);
      upG(p.r, 160, 60);

      // Draw Bag Chain
      ctx.save();
      ctx.translate(cx, 0);
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.rotate(p.a);
      ctx.lineTo(0, cy);
      ctx.strokeStyle = '#334155';
      ctx.lineWidth = 6;
      ctx.lineCap = 'round';
      ctx.stroke();
      ctx.fillStyle = '#475569';
      ctx.beginPath();
      ctx.arc(0, cy, 8, 0, Math.PI * 2);
      ctx.fill();

      // Draw Bag Body
      ctx.translate(0, cy);
      const bagW = 140;
      const bagH = 280;
      ctx.fillStyle = '#9f1239';
      drawRoundedRect(ctx, -bagW / 2, 0, bagW, bagH, 30);
      ctx.fillStyle = '#be123c';
      drawRoundedRect(ctx, -bagW / 2 + 5, 0, bagW - 10, bagH - 5, 25);
      
      // Bag Highlight
      ctx.fillStyle = 'rgba(255,255,255,0.1)';
      ctx.beginPath();
      ctx.ellipse(-30, bagH / 2, 15, bagH / 3, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      // Draw Splashes (Particles)
      for (let i = splashes.current.length - 1; i >= 0; i--) {
        const s = splashes.current[i];
        s.x += s.vx;
        s.y += s.vy;
        s.a -= 0.05;
        if (s.a <= 0) splashes.current.splice(i, 1);
        else {
          ctx.globalAlpha = s.a;
          ctx.fillStyle = s.color;
          ctx.beginPath();
          ctx.arc(s.x, s.y, s.size, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = 1;
        }
      }

      // Draw Gloves (Replaced with SVG)
      ctx.save();
      ctx.translate(cx, cy + 180);
      
      const drawGlove = (g: any, isLeft: boolean) => {
        ctx.save();
        ctx.translate(g.x, 0);
        
        // Mirror for right hand
        if (!isLeft) ctx.scale(-1, 1);
        
        // Only draw if image is loaded
        if (gloveImg.complete) {
            // SVG Aspect Ratio is approx 35x27. 
            // Scaling up 1.8x to match original glove size approx (~63px width)
            const gW = 63; 
            const gH = 50; 
            // Centering logic: -gW/2 + slight offset to align wrist with 0
            ctx.drawImage(gloveImg, -30, -25, gW, gH);
        }
        
        ctx.restore();
      };
      
      drawGlove(p.l, true);
      drawGlove(p.r, false);
      ctx.restore();

      f = requestAnimationFrame(render);
    };

    render();

    const hit = (e: any) => {
      e.preventDefault();
      const pos = getEventPos(e, c);
      const cx = c.width / 2;
      const cy = c.height / 4;
      const bagCenterY = cy + 140;

      if (pos.x < cx) {
        p.l.s = 'p';
        p.v -= 0.12;
      } else {
        p.r.s = 'p';
        p.v += 0.12;
      }

      for (let i = 0; i < 15; i++) {
        splashes.current.push({
          x: cx + (Math.random() - 0.5) * 40,
          y: bagCenterY + (Math.random() - 0.5) * 40,
          vx: (Math.random() - 0.5) * 15,
          vy: (Math.random() - 0.5) * 15,
          size: Math.random() * 5 + 2,
          a: 1,
          color: Math.random() > 0.7 ? '#fcd34d' : '#ffffff'
        });
      }
      if (navigator.vibrate) navigator.vibrate(10);
    };

    c.addEventListener('mousedown', hit);
    c.addEventListener('touchstart', hit, { passive: false });

    return () => {
      window.removeEventListener('resize', rsz);
      c.removeEventListener('mousedown', hit);
      c.removeEventListener('touchstart', hit);
      cancelAnimationFrame(f);
      URL.revokeObjectURL(url); // Cleanup memory
    };

  }, [isDarkMode]);

  return <canvas ref={cvs} className="w-full h-full touch-none cursor-pointer" />;
};

const BulbGame = () => {
  const cvs = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = cvs.current; if (!c) return; const ctx = c.getContext('2d'); if (!ctx) return; let f: number; const s = { on: false, a: 0, v: 0 };
    const rsz = () => { c.width = window.innerWidth; c.height = window.innerHeight; }; rsz(); window.addEventListener('resize', rsz);
    const render = () => {
      const { width: w, height: h } = c; const cx = w / 2; const wl = h * 0.3; s.v += -0.005 * Math.sin(s.a); s.v *= 0.98; s.a += s.v;
      if (s.on) { const lx = cx + Math.sin(s.a) * (wl + 60); const ly = Math.cos(s.a) * (wl + 60); const g = ctx.createRadialGradient(lx, ly, 20, lx, ly, Math.max(w, h)); g.addColorStop(0, '#fefce8'); g.addColorStop(0.15, '#facc15'); g.addColorStop(1, '#1a1a1a'); ctx.fillStyle = g; } else { ctx.fillStyle = '#0f0f10'; }
      ctx.fillRect(0, 0, w, h); if (!s.on) { ctx.fillStyle = 'rgba(255,255,255,0.2)'; ctx.textAlign = 'center'; ctx.font = '14px sans-serif'; ctx.fillText("Tap to switch on", cx, h - 50); }
      ctx.save(); ctx.translate(cx, 0); ctx.rotate(s.a); ctx.strokeStyle = '#52525b'; ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(0, wl); ctx.stroke(); ctx.translate(0, wl); const sw = 26; const sh = 30; ctx.fillStyle = '#a1a1aa'; ctx.fillRect(-sw / 2, 0, sw, sh); ctx.beginPath(); ctx.strokeStyle = '#71717a'; ctx.lineWidth = 2; for (let i = 8; i < sh; i += 6) { ctx.moveTo(-sw / 2, i); ctx.lineTo(sw / 2, i - 2); } ctx.stroke();
      const bh = 55; const br = 45; ctx.beginPath(); ctx.moveTo(-sw / 2 + 2, sh); ctx.bezierCurveTo(-sw / 2, sh + 10, -br, sh + 15, -br, sh + bh); ctx.arc(0, sh + bh, br, Math.PI, 0, true); ctx.bezierCurveTo(br, sh + 15, sw / 2, sh + 10, sw / 2 - 2, sh); ctx.closePath();
      if (s.on) { ctx.shadowBlur = 60; ctx.shadowColor = '#facc15'; ctx.fillStyle = 'rgba(254, 249, 195, 0.95)'; ctx.fill(); ctx.shadowBlur = 0; } else { ctx.fillStyle = 'rgba(255, 255, 255, 0.05)'; ctx.fill(); }
      ctx.strokeStyle = s.on ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.3)'; ctx.lineWidth = 1.5; ctx.stroke(); const fy = sh + 15; ctx.beginPath(); ctx.moveTo(-8, sh); ctx.lineTo(-8, fy); ctx.moveTo(8, sh); ctx.lineTo(8, fy); ctx.strokeStyle = '#71717a'; ctx.lineWidth = 1; ctx.stroke(); ctx.beginPath(); ctx.moveTo(-8, fy); ctx.lineTo(-4, fy + 18); ctx.lineTo(0, fy + 2); ctx.lineTo(4, fy + 18); ctx.lineTo(8, fy);
      if (s.on) { ctx.strokeStyle = '#fff'; ctx.lineWidth = 3; ctx.shadowBlur = 15; ctx.shadowColor = '#fff'; ctx.lineCap = 'round'; ctx.stroke(); ctx.shadowBlur = 0; } else { ctx.strokeStyle = '#52525b'; ctx.lineWidth = 1; ctx.stroke(); }
      ctx.beginPath(); ctx.arc(0, sh + bh, br, Math.PI * 1.1, Math.PI * 1.35); ctx.strokeStyle = 'rgba(255,255,255,0.3)'; ctx.lineWidth = 4; ctx.lineCap = 'round'; ctx.stroke(); ctx.restore(); f = requestAnimationFrame(render);
    };
    render();
    const t = (e: any) => { e.preventDefault(); s.on = !s.on; s.v += (Math.random() - 0.5) * 0.08; if (navigator.vibrate) navigator.vibrate(5); };
    c.addEventListener('mousedown', t); c.addEventListener('touchstart', t, { passive: false });
    return () => { cancelAnimationFrame(f); };
  }, []);
  return <canvas ref={cvs} className="w-full h-full touch-none cursor-pointer" />;
};

const NewtonsCradleGame = ({ isDarkMode }: { isDarkMode: boolean }) => {
  const cvs = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = cvs.current; if (!c) return; const ctx = c.getContext('2d'); if (!ctx) return; let f: number; const balls = Array(5).fill(0).map((_, i) => ({ i, a: 0, v: 0, x: 0, y: 0 }));
    const rsz = () => { c.width = window.innerWidth; c.height = window.innerHeight; }; rsz(); window.addEventListener('resize', rsz);
    let dragIdx = -1;
    const render = () => {
      const { width: w, height: h } = c; const cx = w / 2; const len = h * 0.4; const rad = Math.min(w / 12, 30); const diameter = rad * 2;
      ctx.fillStyle = isDarkMode ? '#0f172a' : '#fff'; ctx.fillRect(0, 0, w, h);
      balls.forEach((b, i) => { if (dragIdx !== i) { b.v += -0.006 * Math.sin(b.a); b.v *= 0.995; b.a += b.v; } });
      for (let k = 0; k < 8; k++) { for (let i = 0; i < 4; i++) { const b1 = balls[i]; const b2 = balls[i + 1]; if (b2.a < b1.a) { const o = (b1.a - b2.a) * 0.5; if (dragIdx !== i) b1.a -= o; if (dragIdx !== i + 1) b2.a += o; const v1 = b1.v; const v2 = b2.v; if (v1 > v2) { b1.v = v2; b2.v = v1; if (k === 0 && Math.abs(v1 - v2) > 0.002 && navigator.vibrate) navigator.vibrate(2); } } } }
      ctx.strokeStyle = isDarkMode ? '#475569' : '#334155'; ctx.lineWidth = 4; ctx.beginPath(); ctx.moveTo(cx - diameter * 2, 50); ctx.lineTo(cx + diameter * 2, 50); ctx.stroke();
      balls.forEach((b, i) => { const ax = cx + (i - 2) * diameter; b.x = ax + Math.sin(b.a) * len; b.y = 50 + Math.cos(b.a) * len; ctx.beginPath(); ctx.moveTo(ax, 50); ctx.lineTo(b.x, b.y); ctx.strokeStyle = '#94a3b8'; ctx.lineWidth = 1.5; ctx.stroke(); ctx.beginPath(); ctx.arc(b.x, b.y, rad - 1, 0, 7); ctx.fillStyle = isDarkMode ? '#94a3b8' : '#334155'; ctx.fill(); });
      f = requestAnimationFrame(render);
    };
    render();
    const hs = (e: any) => { const p = getEventPos(e, c); let m = 1000; balls.forEach((b, i) => { const d = Math.hypot(p.x - b.x, p.y - b.y); if (d < 50 && d < m) { dragIdx = i; m = d; } }); if (dragIdx !== -1) balls[dragIdx].v = 0; };
    const hm = (e: any) => { if (dragIdx === -1) return; const p = getEventPos(e, c); const cx = c.width / 2; const rad = Math.min(c.width / 12, 30); const ax = cx + (dragIdx - 2) * (rad * 2); let a = Math.atan2(p.x - ax, p.y - 50); balls[dragIdx].a = Math.max(-1.5, Math.min(1.5, a)); balls[dragIdx].v = 0; };
    const he = () => { dragIdx = -1; };
    c.addEventListener('mousedown', hs); c.addEventListener('mousemove', hm); window.addEventListener('mouseup', he); c.addEventListener('touchstart', hs, { passive: false }); c.addEventListener('touchmove', hm, { passive: false }); window.addEventListener('touchend', he);
    return () => cancelAnimationFrame(f);
  }, [isDarkMode]);
  return <canvas ref={cvs} className="w-full h-full cursor-grab active:cursor-grabbing touch-none" />;
};

const BreathPacerGame = ({ isDarkMode }: { isDarkMode: boolean }) => {
  const MODES = [{ id: 'relax', name: 'Relaxation', ratio: '4-2-6', inhale: 4, hold: 2, exhale: 6, desc: 'Stress Relief' }, { id: 'focus', name: 'Focus', ratio: '4-4-4', inhale: 4, hold: 4, exhale: 4, desc: 'Mental Clarity' }, { id: 'strength', name: 'Lung Strength', ratio: '6-3-6', inhale: 6, hold: 3, exhale: 6, desc: 'Breath Control' }, { id: 'anxiety', name: 'Anxiety', ratio: '4-0-8', inhale: 4, hold: 0, exhale: 8, desc: 'Panic Relief' },];
  const [isActive, setIsActive] = useState(false); const [mode, setMode] = useState(MODES[0]); const [phase, setPhase] = useState<'Inhale' | 'Hold' | 'Exhale' | 'Ready'>('Ready'); const [sessionTime, setSessionTime] = useState(0); const [guidanceText, setGuidanceText] = useState('Ready'); const stRef = useRef(0); const afRef = useRef<number>();
  useEffect(() => {
    if (!isActive) { cancelAnimationFrame(afRef.current!); return; } stRef.current = Date.now() - (sessionTime * 1000);
    const loop = () => { const el = (Date.now() - stRef.current) / 1000; setSessionTime(Math.floor(el)); const tc = mode.inhale + mode.hold + mode.exhale; const lt = el % tc; if (lt < mode.inhale) { if (phase !== 'Inhale') setPhase('Inhale'); setGuidanceText('Inhale'); } else if (lt < mode.inhale + mode.hold) { if (phase !== 'Hold') setPhase('Hold'); setGuidanceText('Hold'); } else { if (phase !== 'Exhale') setPhase('Exhale'); setGuidanceText('Exhale'); } afRef.current = requestAnimationFrame(loop); }; loop(); return () => cancelAnimationFrame(afRef.current!);
  }, [isActive, mode]);
  const handleStop = () => { setIsActive(false); setPhase('Ready'); setGuidanceText('Paused'); }; const handleReset = () => { setIsActive(false); setSessionTime(0); setPhase('Ready'); setGuidanceText('Ready'); };
  return (
    <div className={`w-full h-full flex flex-col bg-slate-50 relative overflow-hidden ${isDarkMode ? 'bg-slate-900' : 'bg-slate-50'}`}>
      <div className="flex-1 flex flex-col items-center justify-center relative pb-20">
        <div className="relative flex items-center justify-center"><div className={`absolute w-80 h-80 rounded-full transition-colors duration-1000 opacity-20 blur-3xl ${phase === 'Inhale' ? 'bg-blue-300' : phase === 'Hold' ? 'bg-yellow-300' : phase === 'Exhale' ? 'bg-green-300' : (isDarkMode ? 'bg-slate-800' : 'bg-slate-200')}`}></div><div className={`w-60 h-60 rounded-full flex items-center justify-center transition-all duration-1000 ease-in-out shadow-xl ${phase === 'Inhale' ? 'bg-blue-400 scale-110' : phase === 'Hold' ? 'bg-yellow-400 scale-110' : phase === 'Exhale' ? 'bg-green-400 scale-75' : (isDarkMode ? 'bg-slate-800 scale-90' : 'bg-slate-200 scale-90')}`} style={{ transitionDuration: phase === 'Inhale' ? `${mode.inhale}s` : phase === 'Exhale' ? `${mode.exhale}s` : '0.5s' }}><div className={`text-3xl font-bold tracking-widest uppercase ${isDarkMode ? 'text-white' : 'text-slate-700'}`}>{guidanceText}</div></div></div>
        <div className="flex flex-col items-center mt-12 gap-3"><div className={`text-sm font-bold uppercase tracking-widest ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>{mode.name}</div><div className={`px-6 py-2 rounded-full font-mono text-xl font-medium shadow-sm border ${isDarkMode ? 'bg-slate-800 text-slate-300 border-slate-700' : 'bg-white text-slate-600 border-slate-200'}`}>{Math.floor(sessionTime / 60).toString().padStart(2, '0')}:{Math.floor(sessionTime % 60).toString().padStart(2, '0')}</div></div>
      </div>
      <div className={`backdrop-blur-md border-t p-6 z-20 rounded-t-3xl shadow-[0_-10px_40px_rgba(0,0,0,0.05)] ${isDarkMode ? 'bg-slate-900/90 border-slate-800' : 'bg-white/90 border-slate-100'}`}>
        <div className="flex justify-center flex-wrap gap-2 mb-8">{MODES.map(m => (<button key={m.id} onClick={() => { setMode(m); handleReset(); }} className={`px-4 py-2 rounded-full text-xs font-bold border transition-all whitespace-nowrap ${mode.id === m.id ? 'bg-indigo-600 text-white border-indigo-600 shadow-md shadow-indigo-200' : (isDarkMode ? 'bg-slate-800 text-slate-400 border-slate-700 hover:border-indigo-500' : 'bg-white text-slate-500 border-slate-200 hover:border-indigo-300 hover:text-indigo-600')}`}>{m.name} <span className="opacity-70 font-normal ml-1">({m.ratio})</span></button>))}</div>
        <div className="flex justify-center gap-4">{!isActive ? <button onClick={() => setIsActive(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-3 rounded-full flex items-center gap-2 font-bold transition-all active:scale-95 shadow-lg shadow-indigo-200"><Play className="w-5 h-5 fill-current" /> Start</button> : <button onClick={handleStop} className="bg-amber-500 hover:bg-amber-600 text-white px-8 py-3 rounded-full flex items-center gap-2 font-bold transition-all active:scale-95 shadow-lg shadow-amber-200"><Square className="w-5 h-5 fill-current" /> Stop</button>}<button onClick={handleReset} className={`p-3 rounded-full transition-all active:scale-95 ${isDarkMode ? 'bg-slate-800 text-slate-400 hover:bg-slate-700' : 'bg-slate-100 hover:bg-slate-200 text-slate-600'}`}><RotateCcw className="w-5 h-5" /></button></div>
      </div>
    </div>
  );
};
const FireworksGame = () => {
  const cvs = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = cvs.current; if (!c) return; const ctx = c.getContext('2d'); if (!ctx) return; let f: number; let ps: any[] = [];
    const rsz = () => { c.width = window.innerWidth; c.height = window.innerHeight; }; rsz(); window.addEventListener('resize', rsz);
    const render = () => { ctx.fillStyle = 'rgba(0, 0, 0, 0.1)'; ctx.fillRect(0, 0, c.width, c.height); ps.forEach((p, i) => { p.x += p.vx; p.y += p.vy; p.vy += 0.05; p.a -= p.d; ctx.beginPath(); ctx.arc(p.x, p.y, 2, 0, 7); ctx.fillStyle = `hsla(${p.h},100%,50%,${p.a})`; ctx.fill(); if (p.a <= 0) ps.splice(i, 1); }); f = requestAnimationFrame(render); }; render();
    const tap = (e: any) => { const p = getEventPos(e, c); const h = Math.random() * 360; for (let i = 0; i < 50; i++) { const a = Math.random() * 7; const s = Math.random() * 4 + 2; ps.push({ x: p.x, y: p.y, vx: Math.cos(a) * s, vy: Math.sin(a) * s, h, a: 1, d: Math.random() * 0.015 + 0.005 }); } };
    c.addEventListener('mousedown', tap); c.addEventListener('touchstart', tap, { passive: false });
    return () => cancelAnimationFrame(f);
  }, []);
  return <div className="w-full h-full relative"><canvas ref={cvs} className="w-full h-full bg-black cursor-crosshair touch-none" /><div className="absolute bottom-8 w-full text-center pointer-events-none opacity-60 text-white font-medium animate-pulse">Touch to Spark</div></div>;
};

const GravityParticlesGame = () => {
  const cvs = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = cvs.current; if (!c) return; const ctx = c.getContext('2d'); if (!ctx) return; let f: number; const ps = Array(2000).fill(0).map(() => ({ x: Math.random() * window.innerWidth, y: Math.random() * window.innerHeight, vx: 0, vy: 0, hue: Math.random() * 60 + 200 })); let mx = -1000, my = -1000;
    const rsz = () => { c.width = window.innerWidth; c.height = window.innerHeight; }; rsz(); window.addEventListener('resize', rsz);
    const render = () => {
      ctx.fillStyle = 'rgba(10, 10, 20, 0.2)'; ctx.fillRect(0, 0, c.width, c.height); ctx.globalCompositeOperation = 'lighter';
      ps.forEach(p => { const dx = mx - p.x; const dy = my - p.y; const dist = Math.hypot(dx, dy); const force = Math.min(300 / (dist * dist + 1), 0.5); if (dist < 500) { p.vx += dx * force; p.vy += dy * force; } p.vx *= 0.96; p.vy *= 0.96; p.x += p.vx; p.y += p.vy; const speed = Math.hypot(p.vx, p.vy); const bright = Math.min(50 + speed * 20, 100); ctx.fillStyle = `hsla(${p.hue + speed * 5}, 80%, ${bright}%, 0.8)`; ctx.beginPath(); ctx.arc(p.x, p.y, 1.5, 0, Math.PI * 2); ctx.fill(); }); ctx.globalCompositeOperation = 'source-over'; f = requestAnimationFrame(render);
    };
    render(); const mv = (e: any) => { const p = getEventPos(e, c); mx = p.x; my = p.y; };
    c.addEventListener('mousemove', mv); c.addEventListener('touchmove', mv, { passive: false });
    return () => cancelAnimationFrame(f);
  }, []);
  return <canvas ref={cvs} className="w-full h-full bg-[#0a0a14] touch-none" />;
};

const KaleidoscopeGame = () => {
  const cvs = useRef<HTMLCanvasElement>(null); const rot = useRef(false); const ang = useRef(0);
  useEffect(() => {
    const c = cvs.current; if (!c) return; const ctx = c.getContext('2d'); if (!ctx) return; let f: number; const gems = Array(150).fill(0).map(() => ({ x: (Math.random() - 0.5) * 300, y: (Math.random() - 0.5) * 300, r: Math.random() * 15 + 5, c: `hsla(${Math.random() * 360},80%,60%,0.8)` }));
    const rsz = () => { c.width = window.innerWidth; c.height = window.innerHeight; }; rsz(); window.addEventListener('resize', rsz);
    const render = () => { if (rot.current) ang.current += 0.01; ctx.fillStyle = '#111'; ctx.fillRect(0, 0, c.width, c.height); const sl = 8; const st = (Math.PI * 2) / sl; ctx.translate(c.width / 2, c.height / 2); for (let i = 0; i < sl; i++) { ctx.save(); ctx.rotate(i * st); if (i % 2 !== 0) ctx.scale(1, -1); ctx.beginPath(); ctx.moveTo(0, 0); ctx.arc(0, 0, Math.max(c.width, c.height), -st / 2 - 0.01, st / 2 + 0.01); ctx.clip(); ctx.rotate(ang.current); gems.forEach(g => { ctx.fillStyle = g.c; ctx.beginPath(); ctx.arc(g.x, g.y, g.r, 0, 7); ctx.fill(); }); ctx.restore(); } ctx.translate(-c.width / 2, -c.height / 2); f = requestAnimationFrame(render); }; render(); return () => cancelAnimationFrame(f);
  }, []);
  return (
    <div className="absolute inset-0 z-[0] w-full h-full bg-black select-none"><canvas ref={cvs} className="w-full h-full block" /><div className="absolute bottom-10 left-0 right-0 flex justify-center pointer-events-none"><button className="pointer-events-auto bg-white/10 backdrop-blur-md border border-white/20 rounded-full p-6 active:scale-95 select-none touch-none" style={{ touchAction: 'none', userSelect: 'none', WebkitUserSelect: 'none' }} onMouseDown={() => rot.current = true} onMouseUp={() => rot.current = false} onMouseLeave={() => rot.current = false} onTouchStart={(e) => { e.preventDefault(); rot.current = true }} onTouchEnd={(e) => { e.preventDefault(); rot.current = false }}><RotateCw className="w-8 h-8 text-white/80" /></button></div></div>
  );
};

const RippleGame = () => {
  const cvs = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = cvs.current; if (!c) return; const ctx = c.getContext('2d'); if (!ctx) return; let f: number; let rs: any[] = [];
    const rsz = () => { c.width = window.innerWidth; c.height = window.innerHeight; }; rsz(); window.addEventListener('resize', rsz);
    const render = () => { const g = ctx.createLinearGradient(0, 0, 0, c.height); g.addColorStop(0, '#0f172a'); g.addColorStop(1, '#0ea5e9'); ctx.fillStyle = g; ctx.fillRect(0, 0, c.width, c.height); rs.forEach((r, i) => { r.r += 1.5; r.a -= 0.005; ctx.beginPath(); ctx.arc(r.x, r.y, r.r, 0, 7); ctx.strokeStyle = `rgba(255,255,255,${r.a})`; ctx.lineWidth = 2; ctx.stroke(); if (r.a <= 0) rs.splice(i, 1); }); f = requestAnimationFrame(render); };
    render(); const sp = (e: any) => { const p = getEventPos(e, c); rs.push({ x: p.x, y: p.y, r: 0, a: 1 }); }; c.addEventListener('mousedown', sp); c.addEventListener('touchstart', sp, { passive: false });
    return () => cancelAnimationFrame(f);
  }, []);
  return <canvas ref={cvs} className="w-full h-full touch-none" />;
};

const VaseSmashGame = ({ isDarkMode }: { isDarkMode: boolean }) => {
  const cvs = useRef<HTMLCanvasElement>(null); const sRef = useRef({ p: 'idle', y: 0, s: [] as any[] });
  useEffect(() => {
    const c = cvs.current; if (!c) return; const ctx = c.getContext('2d'); if (!ctx) return; let f: number;
    const rsz = () => { c.width = window.innerWidth; c.height = window.innerHeight; sRef.current.y = c.height / 2; }; rsz(); window.addEventListener('resize', rsz);
    const render = () => {
      const { width: w, height: h } = c; const s = sRef.current; const cx = w / 2; const fy = h - 100; const standW = 200; const standH = 90; const standTop = fy - standH;
      ctx.fillStyle = isDarkMode ? '#1e293b' : '#f8fafc'; ctx.fillRect(0, 0, w, h); const gs = ctx.createLinearGradient(cx - standW / 2, 0, cx + standW / 2, 0); gs.addColorStop(0, '#64748b'); gs.addColorStop(0.5, '#e2e8f0'); gs.addColorStop(1, '#64748b'); ctx.fillStyle = gs; ctx.fillRect(cx - standW / 2, standTop, standW, standH); ctx.beginPath(); ctx.ellipse(cx, fy, standW / 2, 20, 0, 0, Math.PI); ctx.fill(); ctx.fillStyle = isDarkMode ? '#94a3b8' : '#f1f5f9'; ctx.beginPath(); ctx.ellipse(cx, standTop, standW / 2, 20, 0, 0, Math.PI * 2); ctx.fill(); ctx.strokeStyle = '#cbd5e1'; ctx.stroke();
      if (s.p === 'idle' || s.p === 'rst') { const targetY = standTop; if (s.p === 'rst') { if (s.y < targetY) s.y += (targetY - s.y) * 0.08 + 1.5; else { s.y = targetY; s.p = 'idle'; if (navigator.vibrate) navigator.vibrate(20); } } else { s.y = targetY + Math.sin(Date.now() / 800) * 2; } ctx.save(); ctx.translate(cx, s.y); ctx.fillStyle = 'rgba(0,0,0,0.15)'; ctx.beginPath(); ctx.ellipse(0, 0, 60, 10, 0, 0, Math.PI * 2); ctx.fill(); ctx.scale(1.4, 1.4); const vg = ctx.createRadialGradient(-30, -150, 10, 0, -100, 150); vg.addColorStop(0, '#fdba74'); vg.addColorStop(0.4, '#ea580c'); vg.addColorStop(1, '#7c2d12'); ctx.fillStyle = vg; ctx.beginPath(); ctx.moveTo(-35, 0); ctx.lineTo(35, 0); ctx.bezierCurveTo(50, -10, 90, -40, 90, -100); ctx.bezierCurveTo(90, -180, 30, -200, 40, -240); ctx.bezierCurveTo(50, -250, 50, -260, 0, -260); ctx.bezierCurveTo(-50, -260, -50, -250, -40, -240); ctx.bezierCurveTo(-30, -200, -90, -180, -90, -100); ctx.bezierCurveTo(-90, -40, -50, -10, -35, 0); ctx.fill(); ctx.fillStyle = '#facc15'; ctx.beginPath(); ctx.rect(-40, -240, 80, 10); ctx.fill(); ctx.fillStyle = '#7c2d12'; ctx.beginPath(); ctx.ellipse(0, -260, 35, 8, 0, 0, Math.PI * 2); ctx.fill(); ctx.restore(); } else if (s.p === 'brk') { let act = 0; s.s.forEach(sh => { sh.x += sh.vx; sh.y += sh.vy; sh.vy += 0.15; sh.r += sh.vr; sh.a -= 0.008; if (sh.y > h - 50 && sh.vy > 0) { sh.vy *= -0.3; sh.vx *= 0.9; } if (sh.a > 0) { act++; ctx.save(); ctx.translate(sh.x, sh.y); ctx.rotate(sh.r); ctx.globalAlpha = sh.a; ctx.fillStyle = sh.c; ctx.beginPath(); ctx.moveTo(-sh.s, -sh.s); ctx.lineTo(sh.s, -sh.s * 0.5); ctx.lineTo(sh.s * 0.5, sh.s); ctx.lineTo(-sh.s * 0.8, sh.s * 0.8); ctx.fill(); ctx.restore(); } }); if (act === 0) { s.p = 'rst'; s.y = -300; } }
      f = requestAnimationFrame(render);
    };
    render();
    const tap = (e: any) => { const p = getEventPos(e, c); const s = sRef.current; const cx = c.width / 2; const vaseTop = s.y - 360; const vaseBot = s.y; if (s.p === 'idle' && Math.abs(p.x - cx) < 120 && p.y > vaseTop && p.y < vaseBot + 50) { s.p = 'brk'; s.s = []; for (let i = 0; i < 50; i++) { const oy = Math.random() * -350; const ox = (Math.random() - 0.5) * 140; s.s.push({ x: cx + ox, y: s.y + oy, vx: ox * 0.05 + (Math.random() - 0.5) * 4, vy: (Math.random() - 0.5) * 6 - 2, r: Math.random() * Math.PI, vr: (Math.random() - 0.5) * 0.2, c: Math.random() > 0.2 ? '#ea580c' : '#facc15', a: 1, s: Math.random() * 10 + 5 }); } if (navigator.vibrate) navigator.vibrate([30, 50]); } };
    c.addEventListener('mousedown', tap); c.addEventListener('touchstart', tap, { passive: false });
    return () => { window.removeEventListener('resize', rsz); c.removeEventListener('mousedown', tap); c.removeEventListener('touchstart', tap); cancelAnimationFrame(f); };
  }, [isDarkMode]);
  return <canvas ref={cvs} className="w-full h-full cursor-pointer touch-none" />;
};

const PeelingGame = () => {
  const cvs = useRef<HTMLCanvasElement>(null); const sc = useRef<HTMLCanvasElement | null>(null); const checkCvs = useRef<HTMLCanvasElement | null>(null); const PALETTE = ['#F6C3AE', '#F0743E', '#CDBCDB', '#FDD848', '#AAD59E', '#9AB2D4']; const getPaletteColor = (exclude?: string) => { let color; do { color = PALETTE[Math.floor(Math.random() * PALETTE.length)]; } while (color === exclude); return color; }; const initialTop = getPaletteColor(); const col = useRef({ c: initialTop, r: getPaletteColor(initialTop) }); const ps = useRef<any[]>([]);
  useEffect(() => {
    const c = cvs.current; if (!c) return; const ctx = c.getContext('2d'); if (!ctx) return; const s = document.createElement('canvas'); sc.current = s; const sctx = s.getContext('2d'); if (!sctx) return; const chk = document.createElement('canvas'); checkCvs.current = chk; chk.width = 100; chk.height = 100; const chkCtx = chk.getContext('2d', { willReadFrequently: true }); let f: number;
    const rsz = () => { c.width = window.innerWidth; c.height = window.innerHeight; s.width = c.width; s.height = c.height; sctx.fillStyle = col.current.c; sctx.fillRect(0, 0, s.width, s.height); }; rsz(); window.addEventListener('resize', rsz);
    const render = () => { ctx.fillStyle = col.current.r; ctx.fillRect(0, 0, c.width, c.height); ctx.drawImage(s, 0, 0); for (let i = ps.current.length - 1; i >= 0; i--) { const p = ps.current[i]; p.x += p.vx; p.y += p.vy; p.vy += 0.5; p.a -= 0.02; if (p.a <= 0) ps.current.splice(i, 1); else { ctx.fillStyle = p.c; ctx.globalAlpha = p.a; ctx.beginPath(); ctx.moveTo(p.x, p.y); ctx.lineTo(p.x + p.s, p.y + p.s * 0.2); ctx.lineTo(p.x + p.s * 0.5, p.y + p.s); ctx.fill(); ctx.globalAlpha = 1; } } f = requestAnimationFrame(render); }; render();
    const checkCompletion = () => { if (!sc.current || !chkCtx) return; const w = 100; const h = 100; chkCtx.clearRect(0, 0, w, h); chkCtx.drawImage(sc.current, 0, 0, w, h); const imageData = chkCtx.getImageData(0, 0, w, h); const data = imageData.data; let opaquePixels = 0; for (let i = 3; i < data.length; i += 4) { if (data[i] > 50) opaquePixels++; } if ((opaquePixels / (w * h)) < 0.10) { col.current.c = col.current.r; col.current.r = getPaletteColor(col.current.c); sctx.globalCompositeOperation = 'source-over'; sctx.fillStyle = col.current.c; sctx.fillRect(0, 0, s.width, s.height); if (navigator.vibrate) navigator.vibrate(100); } };
    const scr = (e: any) => { const p = getEventPos(e, c); const sx = sc.current!.getContext('2d')!; sx.globalCompositeOperation = 'destination-out'; sx.beginPath(); sx.arc(p.x, p.y, 45, 0, Math.PI * 2); sx.fill(); if (Math.random() > 0.5) { ps.current.push({ x: p.x, y: p.y, vx: (Math.random() - 0.5) * 12, vy: (Math.random() - 0.5) * 12, s: Math.random() * 15 + 5, c: col.current.c, a: 1 }); } if (Math.random() < 0.1) { checkCompletion(); } };
    c.addEventListener('mousedown', (e) => { c.addEventListener('mousemove', scr); scr(e); }); window.addEventListener('mouseup', () => c.removeEventListener('mousemove', scr)); c.addEventListener('touchstart', (e) => { e.preventDefault(); c.addEventListener('touchmove', scr); scr(e); }, { passive: false }); window.addEventListener('touchend', () => c.removeEventListener('touchmove', scr));
    return () => cancelAnimationFrame(f);
  }, []);
  return <canvas ref={cvs} className="w-full h-full touch-none cursor-crosshair" />;
};

const SmileySqueezeGame = ({ isDarkMode }: { isDarkMode: boolean }) => {
  const cvs = useRef<HTMLCanvasElement>(null); const state = useRef({ squeeze: 0, vel: 0, dragging: false, dragX: 0 });
  useEffect(() => {
    const c = cvs.current; if (!c) return; const ctx = c.getContext("2d"); if (!ctx) return; let f: number;
    const rsz = () => { c.width = window.innerWidth; c.height = window.innerHeight; }; rsz(); window.addEventListener("resize", rsz);
    const render = () => {
      const { width: w, height: h } = c; const s = state.current; const cx = w / 2; const cy = h / 2 - 50; const radius = 120;
      if (!s.dragging) { const force = -0.08 * s.squeeze; s.vel += force; s.vel *= 0.92; s.squeeze += s.vel; if (Math.abs(s.squeeze) < 0.001 && Math.abs(s.vel) < 0.001) s.squeeze = 0; }
      ctx.fillStyle = isDarkMode ? "#1e293b" : "#fffbeb"; ctx.fillRect(0, 0, w, h); const sq = Math.max(0, s.squeeze); const scaleX = 1 - sq * 0.3; const scaleY = 1 + sq * 0.15;
      ctx.save(); ctx.translate(cx, cy + radius + 40); ctx.scale(1 + sq * 0.2, 1); ctx.fillStyle = isDarkMode ? "rgba(0,0,0,0.3)" : "rgba(0,0,0,0.1)"; ctx.beginPath(); ctx.ellipse(0, 0, radius * 0.8, 20, 0, 0, Math.PI * 2); ctx.fill(); ctx.restore();
      ctx.save(); ctx.translate(cx, cy); ctx.scale(scaleX, scaleY); ctx.beginPath(); ctx.arc(0, 0, radius, 0, Math.PI * 2); const grad = ctx.createRadialGradient(-30, -30, 10, 0, 0, radius); if (isDarkMode) { grad.addColorStop(0, "#fbbf24"); grad.addColorStop(1, "#b45309"); } else { grad.addColorStop(0, "#fde047"); grad.addColorStop(1, "#eab308"); } ctx.fillStyle = grad; ctx.fill();
      const eyeY = -30; const eyeX = 40; ctx.fillStyle = "#422006"; ctx.beginPath(); ctx.arc(-eyeX, eyeY, 12, 0, Math.PI * 2); ctx.fill(); ctx.beginPath(); ctx.arc(eyeX, eyeY, 12, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.lineWidth = 8; ctx.lineCap = "round"; ctx.strokeStyle = "#422006"; const smileCurve = 40 * (1 - sq * 1.5); ctx.quadraticCurveTo(0, 20 + smileCurve, -50, 30); ctx.beginPath(); if (sq > 0.5) { ctx.ellipse(0, 50, 15, 20 - sq * 10, 0, 0, Math.PI * 2); ctx.fill(); } else { ctx.arc(0, 30, 50, 0.2, Math.PI - 0.2); ctx.stroke(); } ctx.restore();
      const sliderY = h - 150; const sliderW = Math.min(w * 0.8, 300); const sliderX = cx - sliderW / 2; ctx.lineCap = "round"; ctx.strokeStyle = isDarkMode ? "#334155" : "#e5e7eb"; ctx.lineWidth = 16; ctx.beginPath(); ctx.moveTo(sliderX, sliderY); ctx.lineTo(sliderX + sliderW, sliderY); ctx.stroke();
      const handleX = sliderX + s.squeeze * sliderW; ctx.strokeStyle = "#eab308"; ctx.beginPath(); ctx.moveTo(sliderX, sliderY); ctx.lineTo(handleX, sliderY); ctx.stroke();
      ctx.fillStyle = isDarkMode ? "#cbd5e1" : "#fff"; ctx.shadowBlur = 10; ctx.shadowColor = "rgba(0,0,0,0.1)"; ctx.beginPath(); ctx.arc(handleX, sliderY, 24, 0, Math.PI * 2); ctx.fill(); ctx.shadowBlur = 0;
      ctx.fillStyle = isDarkMode ? "#94a3b8" : "#9ca3af"; ctx.textAlign = "center"; ctx.font = "14px sans-serif"; if (!s.dragging && s.squeeze < 0.1) ctx.fillText("Slide to Squeeze", cx, sliderY + 60);
      f = requestAnimationFrame(render);
    };
    render();
    const hS = (e: any) => { const p = getEventPos(e, c); const { height: h } = c; const sliderY = h - 150; if (Math.abs(p.y - sliderY) < 60) { state.current.dragging = true; hM(e); } };
    const hM = (e: any) => { if (!state.current.dragging) return; const p = getEventPos(e, c); const { width: w } = c; const sliderW = Math.min(w * 0.8, 300); const sliderX = w / 2 - sliderW / 2; let val = (p.x - sliderX) / sliderW; val = Math.max(0, Math.min(1, val)); state.current.squeeze = val; state.current.vel = 0; };
    const hE = () => { state.current.dragging = false; };
    c.addEventListener("mousedown", hS); c.addEventListener("mousemove", hM); window.addEventListener("mouseup", hE); c.addEventListener("touchstart", hS, { passive: false }); c.addEventListener("touchmove", hM, { passive: false }); window.addEventListener("touchend", hE);
    return () => cancelAnimationFrame(f);
  }, [isDarkMode]);
  return <canvas ref={cvs} className="w-full h-full touch-none cursor-grab active:cursor-grabbing" />;
};

const PaperBoatGame = ({ isDarkMode }: { isDarkMode: boolean }) => {
  const cvs = useRef<HTMLCanvasElement>(null);
  // Physics state
  const physics = useRef({
    x: window.innerWidth / 2,
    y: window.innerHeight / 2,
    vx: 0,
    vy: 0,
    angle: 0,
    ripples: [] as {x: number, y: number, r: number, a: number}[]
  });

  useEffect(() => {
    const c = cvs.current; if (!c) return;
    const ctx = c.getContext('2d'); if (!ctx) return;
    let frame: number;

    const resize = () => { c.width = window.innerWidth; c.height = window.innerHeight; };
    resize();
    window.addEventListener('resize', resize);

    const render = () => {
      const { width: w, height: h } = c;
      const p = physics.current;

      // 1. Physics Update
      p.x += p.vx;
      p.y += p.vy;
      
      // Friction (Water resistance)
      p.vx *= 0.96;
      p.vy *= 0.96;

      // Rotation follows velocity
      const speed = Math.hypot(p.vx, p.vy);
      if (speed > 0.1) {
        const targetAngle = Math.atan2(p.vy, p.vx) + Math.PI / 2;
        // Smooth rotation
        let diff = targetAngle - p.angle;
        while (diff < -Math.PI) diff += Math.PI * 2;
        while (diff > Math.PI) diff -= Math.PI * 2;
        p.angle += diff * 0.05;
      }

      // Wall Bounce
      if (p.x < 50 || p.x > w - 50) p.vx *= -0.8;
      if (p.y < 50 || p.y > h - 50) p.vy *= -0.8;
      p.x = Math.max(50, Math.min(w - 50, p.x));
      p.y = Math.max(50, Math.min(h - 50, p.y));

      // 2. Draw Water
      ctx.fillStyle = isDarkMode ? '#1e3a8a' : '#60a5fa';
      ctx.fillRect(0, 0, w, h);

      // 3. Draw Ripples
      for (let i = p.ripples.length - 1; i >= 0; i--) {
        const r = p.ripples[i];
        r.r += 2;
        r.a -= 0.015;
        if (r.a <= 0) {
          p.ripples.splice(i, 1);
        } else {
          ctx.beginPath();
          ctx.arc(r.x, r.y, r.r, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(255, 255, 255, ${r.a})`;
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      }

      // 4. Draw Boat
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.angle);
      
      // Bobbing animation
      const bob = Math.sin(Date.now() / 500) * 3;
      ctx.scale(1 + bob*0.005, 1 + bob*0.005);

      // Boat Shadow
      ctx.fillStyle = 'rgba(0,0,0,0.2)';
      ctx.beginPath();
      ctx.moveTo(0, -45); ctx.lineTo(35, 15); ctx.lineTo(0, 55); ctx.lineTo(-35, 15);
      ctx.fill();

      // Boat Body (White Paper)
      ctx.translate(-5, -5); // Slight offset from shadow
      ctx.fillStyle = '#f8fafc';
      ctx.beginPath();
      ctx.moveTo(0, -45); ctx.lineTo(35, 15); ctx.lineTo(0, 55); ctx.lineTo(-35, 15);
      ctx.fill();

      // Crease lines
      ctx.fillStyle = '#e2e8f0'; 
      ctx.beginPath(); ctx.moveTo(-35, 15); ctx.lineTo(0, 0); ctx.lineTo(35, 15); ctx.fill();
      
      ctx.strokeStyle = '#cbd5e1'; 
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(0, -45); ctx.lineTo(0, 55); ctx.stroke();
      
      ctx.restore();

      frame = requestAnimationFrame(render);
    };
    render();

    // Interaction Handler
    const interact = (e: any) => {
      const pos = getEventPos(e, c);
      const p = physics.current;
      
      // Add ripple at touch point
      p.ripples.push({ x: pos.x, y: pos.y, r: 10, a: 0.6 });

      // Calculate push force
      const dx = p.x - pos.x;
      const dy = p.y - pos.y;
      const dist = Math.hypot(dx, dy);
      
      // Push the boat away if touch is close
      if (dist < 250) {
        const force = (300 - dist) / 300; // Stronger when closer
        const angle = Math.atan2(dy, dx);
        
        p.vx += Math.cos(angle) * force * 1.5;
        p.vy += Math.sin(angle) * force * 1.5;
      }
    };

    c.addEventListener('mousedown', interact);
    c.addEventListener('mousemove', (e) => { if(e.buttons === 1) interact(e); });
    c.addEventListener('touchstart', interact, { passive: false });
    c.addEventListener('touchmove', interact, { passive: false });

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(frame);
    };
  }, [isDarkMode]);

  return (
    <div className="w-full h-full relative">
      <canvas ref={cvs} className="w-full h-full cursor-pointer touch-none" />
      <div className={`absolute bottom-16 w-full text-center pointer-events-none font-medium text-sm uppercase tracking-wider animate-pulse ${isDarkMode ? "text-blue-200" : "text-blue-100"}`}>
        Touch water to push boat
      </div>
    </div>
  );
};

// --- UPDATED GameCard (Shows text even when locked) ---
const GameCard = ({
  title, desc, color, bg, icon, thumbnail, onClick, isDarkMode, locked
}: any) => {
  return (
    <div
      onClick={onClick}
      className={`relative rounded-[2rem] overflow-hidden cursor-pointer transition-all duration-500 ease-out hover:shadow-xl hover:-translate-y-1 w-full aspect-[4/5] group 
      ${isDarkMode ? "hover:shadow-indigo-500/10" : "hover:shadow-black/5"} 
      ${bg}
      ${locked ? "opacity-100" : "opacity-100"} 
      `}
    >
      {/* LOCK ICON (Top Right) */}
      {locked && (
        <div className="absolute top-3 right-3 z-30 bg-black/20 dark:bg-white/20 backdrop-blur-md p-2 rounded-full">
          <Lock size={16} className="text-white" />
        </div>
      )}

      {/* Thumbnail Layer */}
      {/* If locked, we allow full color but maybe a very slight dim that lifts on hover */}
      {thumbnail ? (
        <div className={`absolute top-0 left-0 right-0 h-[65%] w-full p-4 transition-all duration-500 pointer-events-none ${locked ? "opacity-90 group-hover:opacity-100" : ""}`}>
          <div className="w-full h-full relative overflow-hidden rounded-2xl">
             {thumbnail}
          </div>
        </div>
      ) : (
        <div className={`absolute -right-8 -top-8 w-48 h-48 opacity-10 rotate-12 group-hover:rotate-45 transition-transform duration-700 ${color}`}>
          {icon}
        </div>
      )}

      {/* Content Layer (Title & Text) */}
      <div className="absolute inset-0 flex flex-col justify-end p-5 z-20">
        <div>
          <h3 className={`text-lg font-bold mb-1 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-slate-900"}`}>
            {title}
          </h3>
          {/* Always show description now, even if locked */}
          <p className={`font-medium text-xs leading-relaxed line-clamp-2 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
            {desc}
          </p>
        </div>
      </div>
      
      {/* Optional: Subtle lock overlay on the whole card that vanishes on hover */}
      {locked && (
          <div className="absolute inset-0 bg-slate-100/10 dark:bg-slate-900/10 z-10 pointer-events-none transition-opacity duration-300 group-hover:opacity-0" />
      )}
    </div>
  );
};

// --- NEW UPSELL MODAL (UPDATED) ---
const UpsellModal = ({ onClose }: { onClose: () => void }) => (
  <div onClick={onClose} className="fixed inset-0 z-[1400] bg-black/60 flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in duration-200">
    <div onClick={(e) => e.stopPropagation()} className="bg-white dark:bg-slate-900 w-full max-w-sm p-8 rounded-3xl text-center shadow-2xl relative border dark:border-slate-800">
       <button onClick={onClose} className="absolute top-4 right-4 p-2 opacity-50 hover:opacity-100 transition-opacity"><X size={20}/></button>
       <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-full flex items-center justify-center mx-auto mb-5">
         <Lock size={32} />
       </div>
       <h2 className="text-2xl font-bold mb-2 dark:text-white">Unlock Full Peace</h2>
       <p className="text-slate-500 dark:text-slate-400 mb-6 leading-relaxed text-sm">
         Get lifetime access to all 18+ games, offline mode, and zero distractions.
       </p>
       <div className="space-y-3">
         {/* Google Play (First) - USING NEW BADGE */}
         <a 
           href="https://play.google.com/store/apps/details?id=com.pebblemind.app" 
           target="_blank" 
           rel="noreferrer"
           className="block hover:opacity-90 active:scale-95 transition-all"
         >
           <GooglePlayBadge className="w-full h-auto" />
         </a>

         {/* App Store (Second - Disabled/Launching Soon) */}
         <button disabled className="flex items-center justify-center gap-3 w-full py-3.5 bg-slate-100 text-slate-400 border border-slate-200 dark:bg-slate-800 dark:text-slate-500 dark:border-slate-700 rounded-xl font-bold text-sm cursor-not-allowed opacity-70">
           <AppleIcon className="w-5 h-5 opacity-50" /> Launching Soon...
         </button>

         <button onClick={onClose} className="text-sm text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 py-2">Maybe later</button>
       </div>
    </div>
  </div>
);

const MobileNavBar = ({ 
  onHomeClick, 
  onThemeClick, 
  onAboutClick, 
  onReadClick, 
  isDarkMode 
}: { 
  onHomeClick: () => void;
  onThemeClick: () => void;
  onAboutClick: () => void;
  onReadClick: () => void;
  isDarkMode: boolean;
}) => {
  return (
    <div className={`fixed bottom-0 left-0 right-0 h-[88px] pb-6 rounded-t-3xl shadow-[0_-5px_20px_rgba(0,0,0,0.05)] z-[50] flex items-center justify-evenly px-2 transition-colors duration-500 ${isDarkMode ? "bg-slate-900 border-t border-slate-800 text-slate-400" : "bg-white border-t border-slate-100 text-slate-400"}`}>
      <button onClick={onHomeClick} className="p-3 rounded-full transition-all active:scale-95 hover:text-indigo-500">
        <Home className="w-6 h-6" />
      </button>
      <button onClick={onThemeClick} className={`p-3 rounded-full transition-all active:scale-95 ${isDarkMode ? "text-yellow-400" : "text-slate-400 hover:text-slate-600"}`}>
        {isDarkMode ? <Sun className="w-6 h-6" /> : <Moon className="w-6 h-6" />}
      </button>
      <button onClick={onAboutClick} className="p-3 rounded-full transition-all active:scale-95 hover:text-indigo-500">
        <CircleHelp className="w-6 h-6" />
      </button>
      <button onClick={onReadClick} className="p-3 rounded-full transition-all active:scale-95 hover:text-indigo-500">
        <BookOpen className="w-6 h-6" />
      </button>
    </div>
  );
};


// --- FINAL RESPONSIVE WEB PAGE (Mobile Web Optimized) ---
const WebHomePage = ({
  onSelectGame,
  isDarkMode,
  trialGames,
  premiumGames,
  isInstalled,
  onTermsClick,
  isNative
}: any) => {
  const textMain = isDarkMode ? "text-white" : "text-slate-900";
  const textSub = isDarkMode ? "text-slate-400" : "text-slate-500";

  // Split premium games for Desktop Layout (Left/Right)
  const leftPremium = premiumGames.slice(0, 6);
  const rightPremium = premiumGames.slice(6, 12);

  // --- Helper: Desktop Locked Tile (Detailed) ---
  const DesktopLockedTile = ({ game }: { game: any }) => (
    <div className={`relative aspect-[4/5] rounded-2xl overflow-hidden ${game.bg} group cursor-not-allowed transition-all hover:shadow-xl`}>
       <div className="absolute inset-0 flex items-center justify-center transition-all duration-500 group-hover:blur-sm group-hover:scale-105">
           <div className="w-full h-full p-4">{game.thumbnail}</div>
       </div>
       {/* Lock Top Right */}
       <div className="absolute top-3 right-3 z-10 transition-opacity duration-300 group-hover:opacity-0">
           <div className="bg-black/10 dark:bg-white/10 backdrop-blur-md p-2 rounded-full">
             <Lock className="w-4 h-4 text-slate-900 dark:text-white" />
           </div>
       </div>
       <div className="absolute inset-0 bg-white/95 dark:bg-slate-900/95 flex flex-col items-center justify-center p-4 text-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20">
           <h3 className={`font-bold text-sm mb-1 ${textMain}`}>{game.title}</h3>
           <p className={`text-xs font-medium leading-snug line-clamp-3 ${textSub}`}>{game.desc}</p>
       </div>
    </div>
  );

  // --- Helper: Mobile Locked Icon (Small 4x3 Grid Item) ---
  const MobileLockedIcon = ({ game }: { game: any }) => (
    <div className={`aspect-square rounded-xl overflow-hidden ${game.bg} relative shadow-sm`}>
       {/* Full Icon */}
       <div className="absolute inset-0 p-2 opacity-100">{game.thumbnail}</div>
       {/* Tiny Lock Top Right */}
       <div className="absolute top-1 right-1 z-10">
          <div className="bg-black/10 dark:bg-white/10 p-1 rounded-full backdrop-blur-[1px]">
            <Lock size={8} className="text-slate-900 dark:text-white" />
          </div>
       </div>
    </div>
  );

  return (
    <div className="w-full flex flex-col min-h-screen">
      
      {/* 1. HEADER (Sticky) */}
      <nav className={`w-full px-4 md:px-12 h-16 md:h-20 flex items-center justify-between backdrop-blur-md sticky top-0 z-50 border-b ${isDarkMode ? "bg-slate-950/80 border-slate-800" : "bg-white/80 border-slate-100"}`}>
        <div className="flex items-center gap-2">
          <CustomLogo className="w-8 h-8" />
          <span className={`font-bold text-lg tracking-tight ${textMain}`}>PebbleMind</span>
        </div>
        <div className="flex items-center gap-3">
           <a href="#download" className="px-4 py-2 md:px-5 md:py-2.5 rounded-full bg-indigo-600 text-white text-xs md:text-sm font-bold shadow-lg shadow-indigo-200 hover:bg-indigo-700 active:scale-95 transition-all">
             Get App - ₹79
           </a>
           <button onClick={onTermsClick} className={`p-2 rounded-full border transition-all active:scale-95 ${isDarkMode ? "border-slate-700 text-slate-400" : "border-slate-200 text-slate-600"}`}>
             <BookOpen size={18} />
           </button>
        </div>
      </nav>

      {/* 2. HERO */}
      <div className="px-6 md:px-12 pt-12 pb-8 flex flex-col items-center text-center">
        <h1 className={`text-4xl md:text-6xl font-light mb-4 tracking-tight ${textMain}`}>
          Find your <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-purple-500">Simple Space.</span>
        </h1>
        <p className={`text-lg max-w-2xl mx-auto ${textSub}`}>
          A digital sanctuary of digital toys. No scores, no ads, just peace.
        </p>
      </div>

      {/* 3. TRIAL APPS (Playable) - UPDATED FOR TABLET DENSITY */}
      <div className="px-4 md:px-12 pb-8">
        <div className="flex items-center gap-4 mb-6">
          <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-200 dark:via-slate-800 to-transparent"></div>
          <span className={`text-[10px] md:text-xs font-bold uppercase tracking-widest ${textSub}`}>Free Trial Games</span>
          <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-200 dark:via-slate-800 to-transparent"></div>
        </div>
        
        {/* CHANGED THIS LINE: Added sm:grid-cols-3 and md:grid-cols-4 */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 max-w-7xl mx-auto">
          {trialGames.map((game: any) => (
             <div key={game.id} className="transform hover:-translate-y-1 transition-transform duration-300">
                <GameCard {...game} isDarkMode={isDarkMode} onClick={() => onSelectGame(game.id)} locked={false} />
             </div>
          ))}
        </div>
      </div>

      {/* 4. SAFETY GRID (Mobile Optimized 3x2) */}
      <div className={`w-full py-8 px-6 border-y ${isDarkMode ? "bg-slate-900 border-slate-800" : "bg-indigo-50/50 border-indigo-100"}`}>
        <div className="max-w-4xl mx-auto">
           {/* MOBILE: 3 columns, 2 rows. Smaller text. */}
           <div className="grid grid-cols-3 md:flex md:justify-center md:gap-12 gap-y-6 gap-x-2 text-center">
               <div className="flex flex-col items-center gap-1.5">
                  <ShieldCheck className="w-5 h-5 md:w-8 md:h-8 text-emerald-500" />
                  <span className={`font-bold text-[10px] md:text-base ${textMain}`}>Kids Safe</span>
               </div>
               <div className="flex flex-col items-center gap-1.5">
                  <Users className="w-5 h-5 md:w-8 md:h-8 text-blue-500" />
                  <span className={`font-bold text-[10px] md:text-base ${textMain}`}>Elder Friendly</span>
               </div>
               <div className="flex flex-col items-center gap-1.5">
                  <WifiOff className="w-5 h-5 md:w-8 md:h-8 text-slate-500" />
                  <span className={`font-bold text-[10px] md:text-base ${textMain}`}>Offline</span>
               </div>
               <div className="flex flex-col items-center gap-1.5">
                  <Ban className="w-5 h-5 md:w-8 md:h-8 text-rose-500" />
                  <span className={`font-bold text-[10px] md:text-base ${textMain}`}>No Ads</span>
               </div>
               <div className="flex flex-col items-center gap-1.5">
                  <Lock className="w-5 h-5 md:w-8 md:h-8 text-amber-500" />
                  <span className={`font-bold text-[10px] md:text-base ${textMain}`}>Private</span>
               </div>
               <div className="flex flex-col items-center gap-1.5">
                  <Smile className="w-5 h-5 md:w-8 md:h-8 text-indigo-500" />
                  <span className={`font-bold text-[10px] md:text-base ${textMain}`}>Calm</span>
               </div>
           </div>
        </div>
      </div>

      {/* 5. PREMIUM SECTION (Mobile vs Desktop Layouts) */}
      <div id="download" className="px-6 md:px-12 py-12 md:py-24 flex flex-col items-center overflow-hidden">
        
        {/* === MOBILE LAYOUT (< 1024px) === */}
        <div className="lg:hidden w-full max-w-sm flex flex-col gap-8">
           
           {/* Header */}
           <div className="text-center">
              <h2 className={`text-2xl font-bold mb-2 ${textMain}`}>Unlock the Collection</h2>
              <p className={`text-xs ${textSub}`}>Lifetime access to {premiumGames.length} more games.</p>
           </div>

           {/* 4x3 GRID OF ICONS */}
           <div className="grid grid-cols-4 gap-3">
              {premiumGames.map((game: any) => (
                 <MobileLockedIcon key={game.id} game={game} />
              ))}
           </div>

           {/* Pricing & Buttons */}
           <div className="text-center mt-2">
              <p className={`text-[10px] font-bold uppercase tracking-wider opacity-60 mb-1 ${textSub}`}>One-time Purchase</p>
              <div className={`text-5xl font-black mb-6 ${textMain}`}>₹79</div>
              
              <div className="space-y-3">
                 {/* Google Play Button (1st) */}
                 <a 
                   href="https://play.google.com/store/apps/details?id=com.pebblemind.app" 
                   target="_blank" 
                   rel="noreferrer" 
                   className="block hover:opacity-90 active:scale-95 transition-all"
                 >
                   <GooglePlayBadge className="w-full h-auto" />
                 </a>

                 {/* App Store Button (2nd - Disabled) */}
                 <button disabled className="flex items-center justify-center gap-3 w-full py-4 bg-slate-100 text-slate-400 border border-slate-200 dark:bg-slate-800 dark:text-slate-500 dark:border-slate-700 rounded-xl font-bold text-sm cursor-not-allowed opacity-70">
                   <AppleIcon className="w-5 h-5 opacity-50" />
                   Launching Soon...
                 </button>
              </div>
           </div>
        </div>

        {/* === DESKTOP LAYOUT (Large Screens > 1024px) === */}
        <div className="hidden lg:flex flex-row items-center justify-center gap-12 w-full max-w-7xl">
           <div className="grid grid-cols-2 gap-4 w-full flex-1">
              {leftPremium.map((game: any) => <DesktopLockedTile key={game.id} game={game} />)}
           </div>

           <div className="flex flex-col items-center text-center shrink-0 w-full max-w-md z-20">
              <h2 className={`text-5xl font-bold mb-4 tracking-tight ${textMain}`}>Unlock the<br/>Collection</h2>
              <p className={`text-lg mb-10 leading-relaxed ${textSub}`}>Get lifetime access to PebbleMind, Trampoline, Pond Duck, and 12+ more.</p>
              
              <div className="mb-8">
                 <span className={`text-sm font-bold uppercase tracking-[0.2em] ${textSub} opacity-70`}>Lifetime Access</span>
                 <div className={`text-7xl font-black mt-2 tracking-tighter ${textMain}`}>₹79</div>
              </div>
              
              <div className={`inline-flex items-center gap-3 px-6 py-3 rounded-full mb-10 ${isDarkMode ? "bg-amber-900/20 text-amber-200" : "bg-[#FFF8F0] text-amber-900"}`}>
                 <Coffee className="w-5 h-5 text-amber-500" />
                 <span className="text-sm font-bold">Less than the cost of a chocolate bar.</span>
              </div>
              
              <div className="flex flex-col gap-4 w-full max-w-xs">
                 {/* Google Play Button (1st) */}
                 <a 
                   href="https://play.google.com/store/apps/details?id=com.pebblemind.app" 
                   target="_blank" 
                   rel="noreferrer" 
                   className="block hover:opacity-90 hover:-translate-y-1 transition-all shadow-xl hover:shadow-2xl rounded-xl overflow-hidden"
                 >
                   <GooglePlayBadge className="w-full h-auto" />
                 </a>

                 {/* App Store Button (2nd - Disabled) */}
                 <button disabled className="flex items-center justify-center gap-3 w-full py-4 bg-white text-slate-400 border border-slate-200 dark:bg-slate-800 dark:text-slate-500 dark:border-slate-700 rounded-xl font-bold text-lg cursor-not-allowed opacity-60">
                   <AppleIcon className="w-6 h-6 opacity-50" />
                   Launching Soon
                 </button>
              </div>
           </div>

           <div className="grid grid-cols-2 gap-4 w-full flex-1">
              {rightPremium.map((game: any) => <DesktopLockedTile key={game.id} game={game} />)}
           </div>
        </div>

      </div>
      
     {/* 5. Footer (Scrolls with content) */}
      <footer className={`py-12 px-6 text-center border-t transition-colors duration-500 ${isDarkMode ? "bg-slate-900 border-slate-800 text-slate-500" : "bg-white border-slate-100 text-slate-400"}`}>
        <p className="text-xs mb-4">{"© 2024-2025 PebbleMind. All rights reserved."}</p>
        
        {/* Only visible in Mobile App/Native Mode */}
        {isNative && (
          <div className="space-y-1 opacity-60">
            <p className="text-[11px]">{"A small rating helps PebbleMind reach others."}</p>
            <p className="text-[11px]">{"Thank you if you’ve already shared one."}</p>
          </div>
        )}
      </footer>
    </div>
  );
};

const GameWrapper = ({ children, onClose, isDarkMode }: any) => (
    <div className={`fixed inset-0 z-[60] flex flex-col ${isDarkMode ? "bg-slate-950" : "bg-white"} animate-in slide-in-from-bottom-10 duration-300`}>
        <div className={`absolute top-[calc(env(safe-area-inset-top)+1rem)] right-6 z-[300]`}>
            <button onClick={onClose} className={`p-2.5 rounded-full backdrop-blur-md shadow-lg border transition-all active:scale-95 ${isDarkMode ? "bg-slate-900/50 text-white border-slate-700" : "bg-white/50 text-slate-900 border-slate-200"}`}>
                <X className="w-6 h-6" />
            </button>
        </div>
        <div className="flex-1 relative overflow-hidden">{children}</div>
    </div>
);

// --- MODALS (Content Restored) ---

const TermsModal = ({ onClose, isDarkMode }: any) => {
  const bg = isDarkMode ? "bg-slate-900" : "bg-white";
  const text = isDarkMode ? "text-slate-200" : "text-slate-900";
  const subText = isDarkMode ? "text-slate-400" : "text-slate-500";
  
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className={`w-full max-w-md p-6 rounded-3xl shadow-2xl relative ${bg} max-h-[80vh] overflow-y-auto`}>
         <button onClick={onClose} className="absolute top-4 right-4 p-2 opacity-50 hover:opacity-100"><X size={20}/></button>
         <h2 className={`text-2xl font-bold mb-4 ${text}`}>Terms & Privacy</h2>
         <div className="space-y-4">
           <div><h3 className={`font-bold ${text}`}>Medical Disclaimer</h3><p className={`text-sm ${subText}`}>This application is for entertainment and relaxation only. It is not a medical device.</p></div>
           <div><h3 className={`font-bold ${text}`}>Data Privacy</h3><p className={`text-sm ${subText}`}>We do not collect any personal data. All settings are stored locally on your device.</p></div>
           <div><h3 className={`font-bold ${text}`}>Usage</h3><p className={`text-sm ${subText}`}>Use responsibly. Take breaks if needed.</p></div>
         </div>
         <button onClick={onClose} className={`w-full mt-6 py-3 rounded-xl font-bold ${isDarkMode ? "bg-white text-black" : "bg-slate-900 text-white"}`}>I Understand</button>
      </div>
    </div>
  );
};

const ReadmeModal = ({ onClose, isDarkMode, onSupportClick, isInstalled }: any) => {
    const bg = isDarkMode ? "bg-slate-900" : "bg-white";
    const text = isDarkMode ? "text-slate-200" : "text-slate-900";
    const subText = isDarkMode ? "text-slate-400" : "text-slate-500";
    const border = isDarkMode ? "border-slate-800" : "border-slate-100";
    const btnBase = "flex items-center justify-center gap-2 rounded-xl text-xs font-bold transition-all active:scale-95";

    return (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className={`w-full max-w-sm p-6 rounded-3xl shadow-2xl relative ${bg} max-h-[90vh] overflow-y-auto`}>
                <button onClick={onClose} className="absolute top-4 right-4 p-2 opacity-50 hover:opacity-100 transition-opacity"><X size={20} /></button>
                
                {/* Header */}
                <div className="flex items-center gap-3 mb-4">
                    <div className="p-2.5 rounded-xl bg-indigo-100 text-indigo-600 dark:bg-indigo-900/50 dark:text-indigo-400">
                        <CircleHelp size={24} />
                    </div>
                    <h2 className={`text-2xl font-bold ${text}`}>About</h2>
                </div>

                {/* Description */}
                <p className={`${subText} mb-6 leading-relaxed text-sm`}>
                    A digital sanctuary designed to help you find your simple space. No scores, no goals, just pure interaction for your mind.
                </p>

                {/* Features */}
                <div className="space-y-3 mb-8">
                    <div className="flex items-center gap-3"><ShieldCheck className="w-5 h-5 text-emerald-500 shrink-0" /> <span className={`text-sm font-medium ${text}`}>100% Ad-Free & Private</span></div>
                    <div className="flex items-center gap-3"><Users className="w-5 h-5 text-amber-500 shrink-0" /> <span className={`text-sm font-medium ${text}`}>Kids & Elder Friendly</span></div>
                    <div className="flex items-center gap-3"><Ban className="w-5 h-5 text-rose-500 shrink-0" /> <span className={`text-sm font-medium ${text}`}>No Addictive Mechanics</span></div>
                </div>
            </div>
        </div>
    );
};

const SupportModal = ({ onClose, isDarkMode }: any) => (
  <div onClick={onClose} className="fixed inset-0 z-[1300] bg-black/60 flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in duration-200">
    <div 
      onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside
      className={`p-6 rounded-3xl w-full max-w-sm text-center relative shadow-2xl ${isDarkMode ? "bg-slate-900 text-white border border-slate-800" : "bg-white text-slate-900"}`}
    >
       <button onClick={onClose} className="absolute top-4 right-4 p-2 opacity-50 hover:opacity-100 transition-opacity"><X size={20}/></button>
       
       <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-4 text-amber-500">
         <Coffee size={32} />
       </div>
       
       <h3 className="text-xl font-bold mb-2">Support the Dev</h3>
       <p className={`mb-6 text-sm leading-relaxed ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
         If you enjoy PebbleMind, consider buying a coffee to keep the servers running!
       </p>
       
       <a href="https://buymeacoffee.com/pebblemind" target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 w-full bg-[#FFDD00] text-black font-bold py-3.5 rounded-xl mb-3 hover:bg-[#ffe44d] active:scale-95 transition-all">
          <Coffee size={18} strokeWidth={2.5}/> Buy me a Coffee
       </a>
       
       <button onClick={onClose} className="text-sm opacity-50 hover:opacity-100 py-2">Maybe later</button>
    </div>
  </div>
);

// --- In-App Purchase Modal (Updated Text) ---
const InAppPurchaseModal = ({ onClose, isDarkMode }: any) => (
  <div onClick={onClose} className="fixed inset-0 z-[1300] bg-black/60 flex items-center justify-center p-4 backdrop-blur-sm animate-in fade-in duration-200">
    <div 
      onClick={(e) => e.stopPropagation()} 
      className={`p-6 rounded-3xl w-full max-w-sm text-center relative shadow-2xl ${isDarkMode ? "bg-slate-900 text-white border border-slate-800" : "bg-white text-slate-900"}`}
    >
       <button onClick={onClose} className="absolute top-4 right-4 p-2 opacity-50 hover:opacity-100 transition-opacity"><X size={20}/></button>

       <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center mx-auto mb-4 text-indigo-500">
         <ShoppingBag size={32} />
       </div>

       <h3 className="text-xl font-bold mb-2">Premium Support</h3>
       <p className={`mb-6 text-sm leading-relaxed ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
         Unlock extra color themes and support the development of new games.
       </p>

       <button disabled className={`w-full font-bold py-3.5 rounded-xl mb-3 cursor-not-allowed opacity-70 flex items-center justify-center gap-2 ${isDarkMode ? "bg-slate-800 text-slate-400" : "bg-slate-100 text-slate-400"}`}>
          Coming Soon
       </button>
       
       <button onClick={onClose} className="text-sm opacity-50 hover:opacity-100 py-2">Close</button>
    </div>
  </div>
);

/* ================= UPDATED COMPONENTS ================= */

/* SEARCH AND ENSURE ONLY THIS VERSION REMAINS */

const MobileHomeScreen = ({ isDarkMode, games, onGameClick }: any) => {
  return (
    <div className="w-full flex flex-col">
        {/* Logo Section */}
        <div className="pt-16 pb-8 px-6 flex flex-col items-center justify-center animate-in slide-in-from-top-5 duration-500">
          <div className="flex items-center gap-3 mb-2">
              <CustomLogo className="w-10 h-10" />
              <h1 className={`text-2xl font-bold tracking-tight ${isDarkMode ? "text-white" : "text-slate-900"}`}>Pebble<span className={`font-light ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>Mind</span></h1>
          </div>
          <p className={`text-sm font-medium ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>{"Pick something playful for your mind."}</p>
        </div>

        {/* Grid Section */}
        <div className="px-4">
          {/* UPDATED: Added 'md:grid-cols-4' so tablets show 4 items per row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-5xl mx-auto">
             {games.map((game: any) => (
                <GameCard 
                  key={game.id} 
                  {...game} 
                  isDarkMode={isDarkMode} 
                  onClick={() => onGameClick(game.id)} 
                />
             ))}
          </div>
        </div>
        {/* Footer removed from here to fix scope error and duplication */}
    </div>
  );
};


/* ================= UPDATED MAIN APP CONTROLLER ================= */

function App() {
  const isSecretUrl = window.location.pathname.includes('/logs_temp_storage');
  const [isNative] = useState(Capacitor.isNativePlatform() || isSecretUrl);

  const [activeGame, setActiveGame] = useState<string | null>(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showReadme, setShowReadme] = useState(false);
  const [showUpsell, setShowUpsell] = useState(false);
  const [isPremium, setIsPremium] = useState(isNative);
  const mainRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      try { 
        StatusBar.setOverlaysWebView({ overlay: true }); 
        StatusBar.setStyle({ style: isDarkMode ? Style.Dark : Style.Light }); 
      } catch (e) {}
    }
  }, [isDarkMode]);

  const rawGames = [
    { id: "pebble", title: "PebbleMind", desc: "Focus and flow.", bg: isDarkMode ? "bg-slate-800" : "bg-slate-200", thumbnail: <PebbleMindThumbnail isDarkMode={isDarkMode} /> },
    { id: "trampoline", title: "Trampoline", desc: "Jump for joy.", bg: isDarkMode ? "bg-blue-900/20" : "bg-blue-100", thumbnail: <TrampolineThumbnail isDarkMode={isDarkMode} /> },
    { id: "duck-pond", title: "Pond Duck", desc: "Swim and relax.", bg: isDarkMode ? "bg-orange-900/20" : "bg-orange-100", thumbnail: <DuckPondThumbnail isDarkMode={isDarkMode} /> },
    { id: "bubble-pop", title: "Bubble Pop", desc: "Pop away anxiety.", bg: isDarkMode ? "bg-sky-900/20" : "bg-sky-100", thumbnail: <BubblePopThumbnail isDarkMode={isDarkMode} /> },
    { id: "fidget-spinner", title: "Spin", desc: "Momentum physics.", bg: isDarkMode ? "bg-orange-900/20" : "bg-orange-100", thumbnail: <FidgetSpinnerThumbnail isDarkMode={isDarkMode} /> },
    { id: "stone-skip", title: "Skimmer", desc: "Lake ambience.", bg: isDarkMode ? "bg-indigo-900/20" : "bg-indigo-100", thumbnail: <StoneSkipThumbnail isDarkMode={isDarkMode} /> },
    { id: "punching-bag", title: "Release", desc: "Punch it out.", bg: isDarkMode ? "bg-rose-900/20" : "bg-rose-100", thumbnail: <PunchingBagThumbnail isDarkMode={isDarkMode} /> },
    { id: "bulb", title: "Focus", desc: "Simple toggle.", bg: isDarkMode ? "bg-yellow-900/20" : "bg-yellow-100", thumbnail: <BulbThumbnail isDarkMode={isDarkMode} /> },
    { id: "newton", title: "Newton", desc: "Kinetic swing.", bg: isDarkMode ? "bg-slate-800" : "bg-slate-200", thumbnail: <NewtonThumbnail isDarkMode={isDarkMode} /> },
    { id: "breath", title: "Breathe", desc: "4-7-8 Guide.", bg: isDarkMode ? "bg-teal-900/20" : "bg-teal-100", thumbnail: <BreathThumbnail isDarkMode={isDarkMode} /> },
    { id: "fireworks", title: "Spark", desc: "Fireworks display.", bg: isDarkMode ? "bg-purple-900/20" : "bg-purple-100", thumbnail: <FireworksThumbnail isDarkMode={isDarkMode} /> },
    { id: "gravity", title: "Cosmic Flow", desc: "Fluid particles.", bg: isDarkMode ? "bg-blue-900/20" : "bg-blue-100", thumbnail: <GravityThumbnail isDarkMode={isDarkMode} /> },
    { id: "kaleidoscope", title: "Mirrors", desc: "Kaleidoscope.", bg: isDarkMode ? "bg-pink-900/20" : "bg-pink-100", thumbnail: <KaleidoscopeThumbnail isDarkMode={isDarkMode} /> },
    { id: "ripple", title: "Ripple", desc: "Water surface.", bg: isDarkMode ? "bg-cyan-900/20" : "bg-cyan-100", thumbnail: <RippleThumbnail isDarkMode={isDarkMode} /> },
    { id: "vase", title: "Shatter", desc: "Break the vase.", bg: isDarkMode ? "bg-amber-900/20" : "bg-amber-100", thumbnail: <VaseThumbnail isDarkMode={isDarkMode} /> },
    { id: "peel", title: "Peel", desc: "Scratch reveal.", bg: isDarkMode ? "bg-emerald-900/20" : "bg-emerald-100", thumbnail: <PeelThumbnail isDarkMode={isDarkMode} /> },
    { id: "smiley", title: "Stress Ball", desc: "Squeeze & release.", bg: isDarkMode ? "bg-yellow-900/20" : "bg-yellow-100", thumbnail: <SmileyThumbnail isDarkMode={isDarkMode} /> },
    { id: "paper-boat", title: "Paper Boat", desc: "Float and drift.", bg: isDarkMode ? "bg-blue-900" : "bg-blue-200", thumbnail: <PaperBoatThumbnail isDarkMode={isDarkMode} /> },
  ];

  const TRIAL_IDS = ['paper-boat', 'bubble-pop', 'gravity', 'bulb', 'stone-skip', 'fidget-spinner'];
  const games = rawGames.map(g => ({ ...g, locked: !isPremium && !TRIAL_IDS.includes(g.id) }));
  const handleGameClick = (id: string) => { if (isPremium || TRIAL_IDS.includes(id)) setActiveGame(id); else setShowUpsell(true); };

  return (
    <div className={`h-[100dvh] w-full font-sans flex flex-col relative transition-colors duration-500 overflow-hidden ${isDarkMode ? "bg-[#0f172a] text-slate-100" : "bg-[#F8F9FA] text-slate-800"}`}>
      <SmileyBackground isDarkMode={isDarkMode} />
      <main ref={mainRef} className="absolute inset-0 w-full h-full overflow-y-auto scrollbar-hide pb-[100px]">
        {isNative && (
           <MobileHomeScreen isDarkMode={isDarkMode} games={games} onGameClick={handleGameClick} />
        )}
        
        {!isNative && !activeGame && (
  <WebHomePage 
    onSelectGame={handleGameClick} 
    isDarkMode={isDarkMode} 
    trialGames={games.filter(g => TRIAL_IDS.includes(g.id))} 
    premiumGames={games.filter(g => !TRIAL_IDS.includes(g.id))} 
    onTermsClick={() => setShowTerms(true)}
    isNative={isNative} // <--- ADD THIS HERE
  />
)}

        {/* The Game Wrapper (sit on top of the grid or landing page) */}
        {activeGame && (
          <GameWrapper onClose={() => setActiveGame(null)} isDarkMode={isDarkMode}>
            {activeGame === "pebble" && <PebbleMindGame isDarkMode={isDarkMode} />}
            {activeGame === "trampoline" && <TrampolineGame isDarkMode={isDarkMode} />}
            {activeGame === "duck-pond" && <DuckPondGame isDarkMode={isDarkMode} />}
            {activeGame === "bubble-pop" && <BubblePopGame isDarkMode={isDarkMode} />}
            {activeGame === "fidget-spinner" && <FidgetSpinnerGame isDarkMode={isDarkMode} />}
            {activeGame === "stone-skip" && <StoneSkipGame isDarkMode={isDarkMode} />}
            {activeGame === "punching-bag" && <PunchingBagGame isDarkMode={isDarkMode} />}
            {activeGame === "bulb" && <BulbGame />}
            {activeGame === "newton" && <NewtonsCradleGame isDarkMode={isDarkMode} />}
            {activeGame === "breath" && <BreathPacerGame isDarkMode={isDarkMode} />}
            {activeGame === "fireworks" && <FireworksGame />}
            {activeGame === "gravity" && <GravityParticlesGame />}
            {activeGame === "kaleidoscope" && <KaleidoscopeGame />}
            {activeGame === "ripple" && <RippleGame />}
            {activeGame === "vase" && <VaseSmashGame isDarkMode={isDarkMode} />}
            {activeGame === "peel" && <PeelingGame />}
            {activeGame === "smiley" && <SmileySqueezeGame isDarkMode={isDarkMode} />}
            {activeGame === "paper-boat" && <PaperBoatGame isDarkMode={isDarkMode} />}
          </GameWrapper>
        )}

        {/* --- GLOBAL FOOTER --- */}
        <footer className={`py-16 px-6 text-center transition-colors duration-500 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
          <p className="text-[10px] uppercase tracking-widest font-medium opacity-40 mb-4">
            {"© 2024-2025 PebbleMind"}
          </p>
          
          {/* Rating hint: Only shown if isNative is true. 
            This is safe because it is inside the App component where isNative is defined.
          */}
          {isNative && (
            <div className="space-y-1 opacity-60">
              <p className="text-[11px]">{"A small rating helps PebbleMind reach others."}</p>
              <p className="text-[11px]">{"Thank you if you’ve already shared one."}</p>
            </div>
          )}
        </footer>
      </main>

      {/* Navigation only shows in Native list view when no game is active */}
      {isNative && !activeGame && (
        <MobileNavBar 
          onHomeClick={() => { setActiveGame(null); if (mainRef.current) mainRef.current.scrollTop = 0; }} 
          onThemeClick={() => setIsDarkMode(!isDarkMode)} 
          onAboutClick={() => setShowReadme(true)} 
          onReadClick={() => setShowTerms(true)} 
          isDarkMode={isDarkMode} 
        />
      )}

      {showTerms && <TermsModal onClose={() => setShowTerms(false)} isDarkMode={isDarkMode} />}
      {showReadme && <ReadmeModal onClose={() => setShowReadme(false)} isDarkMode={isDarkMode} />}
      {showUpsell && <UpsellModal onClose={() => setShowUpsell(false)} />}
    </div>
  );
}

    export default App;
